// client/src/components/LiveMatch.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../context/SocketContext';
import { useSoundEffects } from '../context/SoundContext';

// ========== Tournament Themes - ألوان أنعم للعين ==========
const TOURNAMENT_THEMES = {
  champions_league: {
    id: 'champions_league',
    name: 'Champions League',
    primary: '#0a2a6b',
    secondary: '#1e4bb8',
    accent: '#d4af37',
    accentAlt: '#f5f5dc',
    gradient: 'linear-gradient(135deg, #0a2a6b 0%, #1e4bb8 50%, #0a2a6b 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(10,42,107,0.9) 0%, rgba(30,75,184,0.7) 100%)',
    glow: 'rgba(212, 175, 55, 0.3)',
    textPrimary: '#ffffff',
    textSecondary: '#d4af37',
    textMuted: '#a8b8d8',
    cardBg: 'rgba(10, 42, 107, 0.6)',
    cardBorder: 'rgba(212, 175, 55, 0.3)',
    buttonBg: 'linear-gradient(135deg, #d4af37 0%, #e6c200 100%)',
    buttonText: '#0a2a6b',
    buttonHover: 'linear-gradient(135deg, #e6c200 0%, #d4af37 100%)',
    goalGradient: 'linear-gradient(135deg, #d4af37 0%, #e6c200 50%, #d4af37 100%)',
    icon: '🏆',
    titleIcon: '🏆',
    hasStars: true,
    starsCount: 50,
    popupBg: 'linear-gradient(135deg, rgba(10,42,107,0.95) 0%, rgba(30,75,184,0.95) 100%)',
    popupBorder: 'rgba(212, 175, 55, 0.4)',
    popupText: '#d4af37',
    headerBar: 'linear-gradient(90deg, #d4af37 0%, #e6c200 50%, #d4af37 100%)',
    homeTeamColor: '#d4af37',
    awayTeamColor: '#f5f5dc',
    eventGoalColor: '#d4af37',
    eventWarningColor: '#e6c200',
  },
  professional_league: {
    id: 'professional_league',
    name: 'Professional League',
    primary: '#0a3d2e',
    secondary: '#1a6b4f',
    accent: '#00b894',
    accentAlt: '#b8ffd9',
    gradient: 'linear-gradient(135deg, #0a3d2e 0%, #1a6b4f 50%, #0a3d2e 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(10,61,46,0.9) 0%, rgba(26,107,79,0.7) 100%)',
    glow: 'rgba(0, 184, 148, 0.3)',
    textPrimary: '#ffffff',
    textSecondary: '#00b894',
    textMuted: '#a8d8c4',
    cardBg: 'rgba(10, 61, 46, 0.6)',
    cardBorder: 'rgba(0, 184, 148, 0.3)',
    buttonBg: 'linear-gradient(135deg, #00b894 0%, #55efc4 100%)',
    buttonText: '#0a3d2e',
    buttonHover: 'linear-gradient(135deg, #55efc4 0%, #00b894 100%)',
    goalGradient: 'linear-gradient(135deg, #00b894 0%, #55efc4 50%, #00b894 100%)',
    icon: '💎',
    titleIcon: '🏆',
    hasDiamonds: true,
    diamondsCount: 40,
    popupBg: 'linear-gradient(135deg, rgba(10,61,46,0.95) 0%, rgba(26,107,79,0.95) 100%)',
    popupBorder: 'rgba(0, 184, 148, 0.4)',
    popupText: '#00b894',
    headerBar: 'linear-gradient(90deg, #00b894 0%, #55efc4 50%, #00b894 100%)',
    homeTeamColor: '#00b894',
    awayTeamColor: '#b8ffd9',
    eventGoalColor: '#00b894',
    eventWarningColor: '#55efc4',
  },
  domestic_league: {
    id: 'domestic_league',
    name: 'Domestic League',
    primary: '#1a1a1a',
    secondary: '#2d2d2d',
    accent: '#c9a227',
    accentAlt: '#f5f5dc',
    gradient: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    gradientSoft: 'linear-gradient(135deg, rgba(26,26,26,0.9) 0%, rgba(45,45,45,0.7) 100%)',
    glow: 'rgba(201, 162, 39, 0.3)',
    textPrimary: '#ffffff',
    textSecondary: '#c9a227',
    textMuted: '#a0a0a0',
    cardBg: 'rgba(26, 26, 26, 0.6)',
    cardBorder: 'rgba(201, 162, 39, 0.3)',
    buttonBg: 'linear-gradient(135deg, #c9a227 0%, #d4af37 100%)',
    buttonText: '#1a1a1a',
    buttonHover: 'linear-gradient(135deg, #d4af37 0%, #c9a227 100%)',
    goalGradient: 'linear-gradient(135deg, #c9a227 0%, #d4af37 50%, #c9a227 100%)',
    icon: '⚽',
    titleIcon: '⚽',
    hasStars: false,
    popupBg: 'linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(45,45,45,0.95) 100%)',
    popupBorder: 'rgba(201, 162, 39, 0.4)',
    popupText: '#c9a227',
    headerBar: 'linear-gradient(90deg, #c9a227 0%, #d4af37 50%, #c9a227 100%)',
    homeTeamColor: '#c9a227',
    awayTeamColor: '#f5f5dc',
    eventGoalColor: '#c9a227',
    eventWarningColor: '#d4af37',
  }
};

const getTournamentTheme = (tournamentType) => {
  return TOURNAMENT_THEMES[tournamentType] || TOURNAMENT_THEMES.domestic_league;
};

const getTournamentTypeFromCompetition = (competition) => {
  if (competition === 'champions') return 'champions_league';
  if (competition === 'europa') return 'professional_league';
  return 'domestic_league';
};

const getTournamentNameFromCompetition = (competition, continent) => {
  if (competition === 'champions') return 'UEFA Champions League';
  if (competition === 'europa') return 'Professional League';
  if (competition === 'friendly') return 'Friendly Match';
  return `${continent || ''} Domestic League`.trim();
};

// ========== Tournament Background Effects ==========
const TournamentBackground = ({ theme }) => {
  if (theme.hasStars) {
    return (
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {[...Array(theme.starsCount)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              fontSize: `${Math.random() * 12 + 8}px`,
              color: theme.accent,
              opacity: 0.3,
            }}
            animate={{
              opacity: [0.2, 0.5, 0.2],
              scale: [0.8, 1.2, 0.8],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          >
            ✦
          </motion.div>
        ))}
      </div>
    );
  }
  
  if (theme.hasDiamonds) {
    return (
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {[...Array(theme.diamondsCount)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              fontSize: `${Math.random() * 12 + 8}px`,
              color: theme.accent,
              opacity: 0.3,
            }}
            animate={{
              opacity: [0.2, 0.5, 0.2],
              rotate: [0, 180, 360],
              scale: [0.9, 1.1, 0.9],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          >
            💠
          </motion.div>
        ))}
      </div>
    );
  }
  
  return null;
};

// ========== Tournament Banner ==========
const TournamentBanner = ({ theme, tournamentName }) => {
  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 100 }}
      className="relative mb-4 overflow-hidden rounded-2xl border-2 shadow-2xl"
      style={{
        background: theme.gradient,
        borderColor: theme.accent,
        boxShadow: `0 0 30px ${theme.glow}`,
      }}
    >
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, ${theme.accent} 0%, transparent 50%), radial-gradient(circle at 80% 50%, ${theme.accent} 0%, transparent 50%)`,
        }}
      />
      <div className="relative flex items-center justify-center gap-4 py-3 px-6">
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ repeat: Infinity, duration: 3 }}
          className="text-3xl md:text-4xl"
        >
          {theme.titleIcon}
        </motion.div>
        <div className="text-center">
          <div 
            className="text-xs md:text-sm uppercase tracking-widest font-semibold"
            style={{ color: theme.textMuted }}
          >
            {tournamentName || theme.name}
          </div>
          <div 
            className="text-lg md:text-2xl font-black tracking-wider"
            style={{ 
              color: theme.textPrimary,
              textShadow: `0 0 20px ${theme.glow}`,
            }}
          >
            {theme.name}
          </div>
        </div>
        <motion.div
          animate={{ rotate: [0, -10, 10, 0] }}
          transition={{ repeat: Infinity, duration: 3 }}
          className="text-3xl md:text-4xl"
        >
          {theme.titleIcon}
        </motion.div>
      </div>
      <div 
        className="h-1 w-full"
        style={{ background: theme.headerBar }}
      />
    </motion.div>
  );
};

// ========== Half Time Animation ==========
const HalfTimeAnimation = ({ onComplete, theme }) => {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(() => onComplete(), 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-md"
      style={{ background: theme.gradient }}
    >
      <TournamentBackground theme={theme} />
      <motion.div
        animate={{ rotate: [0, 10, -10, 10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
        className="text-9xl md:text-9xl mb-6 filter drop-shadow-2xl relative z-10"
      >
        {theme.icon}
      </motion.div>
      <motion.h1
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-4xl md:text-6xl font-extrabold bg-clip-text text-transparent tracking-wider relative z-10"
        style={{ 
          backgroundImage: theme.goalGradient,
        }}
      >
        {theme.name}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-sm mt-3 relative z-10"
        style={{ color: theme.textSecondary }}
      >
        {t('match.halfTime')}
      </motion.p>
    </motion.div>
  );
};

// ========== Event Popup ==========
const EventPopup = ({ type, team, player, onComplete, theme }) => {
  const { t } = useTranslation();
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onComplete();
    }, 3000);
    return () => clearTimeout(timerRef.current);
  }, []);

  const title = t('match.offside');
  const icon = '🚩';

  return (
    <motion.div
      initial={{ scale: 0.85, opacity: 0, y: 20 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.85, opacity: 0, y: 20 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onComplete}
    >
      <div className="relative w-80 md:w-96" onClick={(e) => e.stopPropagation()}>
        <div 
          className="absolute inset-0 -z-10 rounded-2xl blur-xl"
          style={{ background: theme.gradient }}
        />
        <div 
          className="relative overflow-hidden rounded-2xl border bg-white/5 backdrop-blur-xl shadow-2xl"
          style={{ 
            borderColor: theme.popupBorder,
            boxShadow: `0 0 40px ${theme.glow}`,
          }}
        >
          <div className="h-1 w-full" style={{ background: theme.headerBar }} />
          <div className="p-6 text-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 140, damping: 10, delay: 0.1 }}
              className="mx-auto mb-4 text-6xl md:text-7xl drop-shadow-lg"
            >
              {icon}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-white"
            >
              {title}
            </motion.div>
            {player && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-sm mt-2"
                style={{ color: theme.popupText }}
              >
                {t('match.player', { player })}
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xs text-white/80 mt-3"
            >
              {t('match.flagUp')}
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ========== Helper Components ==========
const StatWithArrow = ({ value, diff, label, theme }) => {
  let arrow = null;
  if (diff > 0) arrow = <span className="text-green-500 text-xs ml-1 animate-bounce">▲</span>;
  else if (diff < 0) arrow = <span className="text-red-500 text-xs ml-1 animate-bounce">▼</span>;
  else arrow = <span className="text-xs ml-1" style={{ color: theme.textMuted }}>•</span>;
  return (
    <div 
      className="flex items-center gap-1 px-2 py-1 rounded-lg"
      style={{ background: theme.cardBg }}
    >
      <span className="text-xs font-mono" style={{ color: theme.textMuted }}>{label}</span>
      <span className="font-mono text-sm font-bold text-white">{value}</span>
      {arrow}
    </div>
  );
};

const formations = [
  { id: 'counter', nameKey: 'formation.counter', short: '2-2-1' },
  { id: 'aggressive', nameKey: 'formation.aggressive', short: '2-1-2' },
  { id: 'traditionalAttack', nameKey: 'formation.traditionalAttack', short: '2-2-1' },
  { id: 'traditionalDefense', nameKey: 'formation.traditionalDefense', short: '3-1-1' },
  { id: 'highPress', nameKey: 'formation.highPress', short: '4-3-3' },
  { id: 'advancedDefense', nameKey: 'formation.advancedDefense', short: '5-3-2' }
];

const playingStyles = [
  { id: 'balanced', nameKey: 'match.balanced', icon: '↔️', descKey: 'match.playingStyleDescBalanced' },
  { id: 'long_ball', nameKey: 'match.long_ball', icon: '⚽', descKey: 'match.playingStyleDescLongBall' },
  { id: 'short_pass', nameKey: 'match.short_pass', icon: '🎯', descKey: 'match.playingStyleDescShortPass' }
];

const getAbilityIcon = (ability) => {
  const icons = {
    Playmaker: '🎭',
    'Power Builder': '💪',
    'Super Scorer': '🎯',
    'Penalty Magnet': '⚡',
    'Super Saver': '🧤',
    'Speed Runner': '🏃',
    'Clean Man': '🧼',
  };
  return icons[ability] || '✨';
};

const getPlayerAvatarSrc = (player) => {
  const positionMap = {
    Striker: 'striker',
    Defender: 'defender',
    Midfielder: 'midfielder',
    Goalkeeper: 'goalkeeper',
  };
  const baseName = positionMap[player.position] || 'player';
  if (player.rarity === 'gold' || player.rarity === 'silver' || player.rarity === 'bronze') {
    return `/img/${player.rarity}_${baseName}.jpg`;
  }
  return `/img/${baseName}.jpg`;
};

const calculatePlayerPower = (player) => {
  if (!player) return 0;
  const attack = Number(player.attack) || 0;
  const defense = Number(player.defense) || 0;
  const speed = Number(player.speed) || 0;
  const accuracy = Number(player.accuracy) || 0;
  const strength = Number(player.strength) || 50;
  const tactics = Number(player.tactics) || 50;
  const { position, special_ability } = player;
  let power = 0;
  if (position === 'Striker') {
    power = (speed * 0.4 + accuracy * 0.4 + attack * 0.3 + defense * 0.2 + strength * 0.4 + tactics * 0.3) / 2.0;
  } else if (position === 'Defender') {
    power = (defense * 0.4 + speed * 0.3 + strength * 0.5 + tactics * 0.3 + attack * 0.2 + accuracy * 0.2) / 1.9;
  } else if (position === 'Midfielder') {
    power = (speed * 0.3 + defense * 0.3 + attack * 0.3 + accuracy * 0.3 + strength * 0.4 + tactics * 0.5) / 2.1;
  } else if (position === 'Goalkeeper') {
    power = (attack + defense + speed + accuracy + strength + tactics) / 6;
  } else {
    power = (attack + defense + speed + accuracy + strength + tactics) / 6;
  }
  if (special_ability === 'Playmaker') power *= 1.05;
  else if (special_ability === 'Super Scorer' && position === 'Striker') power *= 1.15;
  else if (special_ability === 'Super Saver' && position === 'Goalkeeper') power *= 1.15;
  else if (special_ability === 'Speed Runner') power *= 1.10;
  return Math.min(100, Math.max(0, power));
};

// ========== SquadPlayer with mobile stats below player name ==========
const SquadPlayer = ({ player, stats, isHighlighted, onClick, redCarded, theme }) => {
  const hasRedCard = redCarded || stats?.red > 0;
  const power = player.playerPower !== undefined ? Math.round(player.playerPower) : Math.round(calculatePlayerPower(player));
  const specialAbility = player.special_ability;

  return (
    <div
      onClick={hasRedCard ? undefined : onClick}
      className={`flex flex-wrap items-start gap-2 rounded-xl p-2 transition-all duration-200 ${
        hasRedCard ? 'opacity-50 bg-red-900/20 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-700/60'
      } ${isHighlighted ? 'ring-2' : ''} ${
        hasRedCard ? 'border border-red-500/50' : 'border'
      }`}
      style={{
        background: isHighlighted ? `${theme.accent}20` : theme.cardBg,
        borderColor: isHighlighted ? theme.accent : theme.cardBorder,
        boxShadow: isHighlighted ? `0 0 20px ${theme.glow}` : 'none',
      }}
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden bg-gray-900 shadow-lg ring-2 ring-white/30">
        <img
          src={getPlayerAvatarSrc(player)}
          alt={player.position}
          className="w-full h-full object-cover object-center"
          onError={(e) => { e.target.onerror = null; e.target.src = '/img/player.jpg'; }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white truncate flex items-center gap-1">
          {player.name}
          {specialAbility && (
            <span className="text-purple-300 text-[10px]" title={specialAbility}>
              {getAbilityIcon(specialAbility)}
            </span>
          )}
        </div>
        <div className="text-xs flex flex-wrap items-center gap-2" style={{ color: theme.textMuted }}>
          <span>{player.position}</span>
          <span style={{ color: theme.textSecondary }}>⚡{power}</span>
          {/* Mobile stats: appear below player name on small screens */}
          <div className="flex flex-wrap gap-1 text-xs md:hidden w-full mt-0.5">
            {stats?.goals > 0 && <span className="bg-yellow-500/20 px-1 py-0.5 rounded-full text-yellow-300">⚽{stats.goals}</span>}
            {stats?.yellow > 0 && <span className="bg-yellow-500/20 px-1 py-0.5 rounded-full text-yellow-200">🟨{stats.yellow}</span>}
            {hasRedCard && <span className="bg-red-500/20 px-1 py-0.5 rounded-full text-red-300">🟥{stats.red}</span>}
            {stats?.injury > 0 && <span className="bg-red-500/20 px-1 py-0.5 rounded-full text-red-300">🩹{stats.injury}</span>}
          </div>
        </div>
      </div>
      {/* Desktop stats: appear on the right side on larger screens */}
      <div className="hidden md:flex gap-1 text-xs flex-shrink-0">
        {stats?.goals > 0 && <span className="bg-yellow-500/20 px-1 py-0.5 rounded-full text-yellow-300">⚽{stats.goals}</span>}
        {stats?.yellow > 0 && <span className="bg-yellow-500/20 px-1 py-0.5 rounded-full text-yellow-200">🟨{stats.yellow}</span>}
        {hasRedCard && <span className="bg-red-500/20 px-1 py-0.5 rounded-full text-red-300">🟥{stats.red}</span>}
        {stats?.injury > 0 && <span className="bg-red-500/20 px-1 py-0.5 rounded-full text-red-300">🩹{stats.injury}</span>}
      </div>
    </div>
  );
};

const EventItem = ({ event, userId, homeUserId, awayUserId, yourSquad, opponentSquad, yourBench, opponentBench, onPlayerClick, isNew, t, isHomeTeam, opponentId, theme }) => {
  let text = '';
  let icon = '';
  let textColor = 'text-white';
  let isMyTeam = false;

  const allYourPlayers = [...yourSquad, ...yourBench];
  const allOppPlayers = [...opponentSquad, ...opponentBench];

  if (event.type === 'goal') {
    const scorerUserId = event.scorerId;
    if (scorerUserId) isMyTeam = scorerUserId === userId;
    else if (event.playerId) isMyTeam = allYourPlayers.some(p => p.id === event.playerId);
    else isMyTeam = (event.team === 'home' && homeUserId === userId) || (event.team === 'away' && awayUserId === userId);
  } else {
    const playerId = event.playerId || event.scorerId || null;
    if (playerId) {
      if (allYourPlayers.some(p => p.id === playerId)) isMyTeam = true;
      else if (allOppPlayers.some(p => p.id === playerId)) isMyTeam = false;
      else isMyTeam = (event.team === 'home' && homeUserId === userId) || (event.team === 'away' && awayUserId === userId);
    } else {
      isMyTeam = (event.team === 'home' && homeUserId === userId) || (event.team === 'away' && awayUserId === userId);
    }
  }
  const involved = isMyTeam;
  const handleClick = () => {
    const playerId = event.playerId || event.scorerId || null;
    if (playerId && onPlayerClick) onPlayerClick(playerId, involved ? 'home' : 'away');
  };

  if (event.type === 'goal') {
    icon = '⚽';
    const home = event.score?.[userId] ?? 0;
    const away = event.score?.[opponentId] ?? 0;
    const who = ((event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam)) ? t('match.you') : t('match.opponent');
    const scorerName = event.player || allYourPlayers.find(p => p.id === event.playerId)?.name || allOppPlayers.find(p => p.id === event.playerId)?.name || '';
    text = `${who} ${t('match.goal')}${scorerName ? ` – ${scorerName}` : ''} (${home}–${away})`;
    textColor = '';
  } else if (event.type === 'yellow') {
    icon = '🟨';
    text = `${t('match.yellowCard')} ${t('match.for')} ${involved ? t('match.you') : t('match.opponent')}${event.player ? ` (${event.player})` : ''}`;
  } else if (event.type === 'red') {
    icon = '🟥';
    text = `${t('match.redCard')} ${t('match.for')} ${involved ? t('match.you') : t('match.opponent')}${event.player ? ` (${event.player})` : ''}`;
    textColor = 'text-red-400 font-bold';
  } else if (event.type === 'injury') {
    icon = '🩹';
    text = `${t('match.injury')} ${t('match.for')} ${involved ? t('match.you') : t('match.opponent')}${event.player ? ` (${event.player})` : ''}`;
  } else if (event.type === 'penalty') {
    icon = '🇵';
    text = `${t('match.penalty')} ${t('match.awardedTo')} ${involved ? t('match.you') : t('match.opponent')}`;
  } else if (event.type === 'penalty_miss') {
    icon = '❌';
    text = `${t('match.penaltyMiss')} (${involved ? t('match.you') : t('match.opponent')})`;
    textColor = '';
  } else if (event.type === 'substitution') {
    icon = '🔄';
    text = `${involved ? t('match.you') : t('match.opponent')} ${t('match.substituted')} ${event.playerIn} → ${event.playerOut}`;
    textColor = 'text-blue-300';
  } else if (event.type === 'postHit') {
    icon = '🎯';
    const who = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam) ? t('match.you') : t('match.opponent');
    const playerName = event.player ? ` (${event.player})` : '';
    text = `${who} ${t('match.postHit')}${playerName}`;
    textColor = '';
  } else if (event.type === 'greatSave') {
    icon = '🧤';
    const who = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam) ? t('match.you') : t('match.opponent');
    const playerName = event.player ? ` (${event.player})` : '';
    text = `${who} ${t('match.greatSave')}${playerName}`;
    textColor = 'text-green-300';
  } else if (event.type === 'missed_chance') {
    icon = '😱';
    const who = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam) ? t('match.you') : t('match.opponent');
    const playerName = event.player ? ` (${event.player})` : '';
    text = `${who} ${t('match.missedChance')}${playerName}`;
    textColor = '';
  } else if (event.type === 'offside') {
    icon = '🚩';
    const who = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam) ? t('match.you') : t('match.opponent');
    const playerName = event.player ? ` (${event.player})` : '';
    text = `${who} ${t('match.offside')}${playerName}`;
    textColor = 'text-blue-300';
  } else {
    icon = '❓';
    text = event.type;
    textColor = 'text-gray-400';
    console.warn('⚠️ Unknown event type:', event.type);
  }

  const getTextColor = () => {
    if (event.type === 'goal') return { color: theme.eventGoalColor };
    if (event.type === 'penalty_miss') return { color: theme.eventWarningColor };
    if (event.type === 'postHit') return { color: theme.eventWarningColor };
    if (event.type === 'missed_chance') return { color: theme.eventWarningColor };
    if (textColor) return { className: textColor };
    return { className: 'text-white' };
  };

  const colorProps = getTextColor();

  return (
    <motion.div
      initial={isNew ? { opacity: 0, x: -20 } : { opacity: 1 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className={`border-b py-2 text-sm flex items-center gap-2 cursor-pointer hover:bg-gray-700/30`}
      style={{ borderColor: `${theme.cardBorder}40`, ...colorProps }}
      onClick={handleClick}
    >
      <span className="font-mono w-10" style={{ color: theme.textSecondary }}>{event.second}"</span>
      <span className="text-xl">{icon}</span>
      <span>{text}</span>
    </motion.div>
  );
};

// ========== MiniPitch ==========
const MiniPitch = ({ homeSquad, awaySquad, homeScore, awayScore, isLive, theme, threatData }) => {
  const getPositionStyle = (position, team, squad, index) => {
    const isHome = team === 'home';
    const group = squad.filter(p => p.position === position);
    const count = group.length;
    const posInGroup = group.indexOf(squad[index]);

    let baseX;
    if (position === 'Goalkeeper') {
      baseX = isHome ? 8 : 92;
    } else if (position === 'Defender') {
      baseX = isHome ? 25 : 75;
    } else if (position === 'Midfielder') {
      baseX = 50;
    } else if (position === 'Striker') {
      baseX = isHome ? 75 : 25;
    } else {
      baseX = 50;
    }

    let baseY = 50;
    if (count > 1) {
      const spread = 60;
      const step = spread / (count - 1);
      const offset = (posInGroup - (count - 1) / 2) * step;
      baseY = 50 + offset;
    }

    return {
      x: Math.min(95, Math.max(5, baseX)),
      y: Math.min(90, Math.max(10, baseY))
    };
  };

  const [homePlayersPos, setHomePlayersPos] = useState(() =>
    homeSquad.map((p, idx) => getPositionStyle(p.position, 'home', homeSquad, idx))
  );
  const [awayPlayersPos, setAwayPlayersPos] = useState(() =>
    awaySquad.map((p, idx) => getPositionStyle(p.position, 'away', awaySquad, idx))
  );
  const [ballPos, setBallPos] = useState({ x: 50, y: 50 });
  const [attackingTeam, setAttackingTeam] = useState(null);

  useEffect(() => {
    const diff = homeScore - awayScore;
    if (diff > 0) setAttackingTeam('home');
    else if (diff < 0) setAttackingTeam('away');
    else setAttackingTeam(null);
  }, [homeScore, awayScore]);

  // ✅ حركة اللاعبين تعتمد على threatData بشكل ذكي ومنطقي حسب المركز
  useEffect(() => {
    if (!isLive || !threatData) return;
    
    const { userThreat, intensity = 30, actionZone = 'midfield' } = threatData;
    const isUserAttacking = userThreat > 55;
    const isUserDefending = userThreat < 45;

    // حركة فريقك (Home)
    setHomePlayersPos(prev => prev.map((pos, i) => {
      const player = homeSquad[i];
      if (player.position === 'Goalkeeper') {
        // الحارس يتقدم قليلاً عند الضغط في نصف الملعب، ويرجع عند الخطر
        const targetX = actionZone === 'own_box' ? 10 : (actionZone === 'opp_box' ? 18 : 8);
        return { x: pos.x + (targetX - pos.x) * 0.1, y: 50 + (Math.random() - 0.5) * 2 };
      }

      let targetX = pos.x;
      // منطق الحركة حسب المركز ومنطقة اللعب
      if (isUserAttacking) {
        if (player.position === 'Striker') targetX = Math.min(88, pos.x + (intensity / 4));
        else if (player.position === 'Midfielder') targetX = Math.min(70, pos.x + (intensity / 6));
        else if (player.position === 'Defender') targetX = Math.min(45, pos.x + (intensity / 12)); // المدافعون يحافظون على الخط
      } else if (isUserDefending) {
        if (player.position === 'Striker') targetX = Math.max(25, pos.x - (intensity / 4));
        else if (player.position === 'Midfielder') targetX = Math.max(35, pos.x - (intensity / 6));
        else if (player.position === 'Defender') targetX = Math.max(12, pos.x - (intensity / 12));
      }

      // إضافة اهتزاز عضوي (Jitter) يزداد مع شدة الضغط لمحاكاة الحماس
      const jitter = (intensity / 100) * 4;
      return {
        x: pos.x + (targetX - pos.x) * 0.15, // حركة سلسة (Smooth Lerp)
        y: Math.min(90, Math.max(10, pos.y + (Math.random() - 0.5) * jitter))
      };
    }));

    // حركة الخصم (Away) - منطق معكوس تماماً
    setAwayPlayersPos(prev => prev.map((pos, i) => {
      const player = awaySquad[i];
      if (player.position === 'Goalkeeper') {
        const targetX = actionZone === 'opp_box' ? 90 : (actionZone === 'own_box' ? 82 : 92);
        return { x: pos.x + (targetX - pos.x) * 0.1, y: 50 + (Math.random() - 0.5) * 2 };
      }

      let targetX = pos.x;
      if (isUserDefending) { // إذا كنت تدافع، الخصم يهاجم
        if (player.position === 'Striker') targetX = Math.max(12, pos.x - (intensity / 4));
        else if (player.position === 'Midfielder') targetX = Math.max(30, pos.x - (intensity / 6));
        else if (player.position === 'Defender') targetX = Math.max(55, pos.x - (intensity / 12));
      } else if (isUserAttacking) { // إذا كنت تهاجم، الخصم يدافع
        if (player.position === 'Striker') targetX = Math.min(75, pos.x + (intensity / 4));
        else if (player.position === 'Midfielder') targetX = Math.min(65, pos.x + (intensity / 6));
        else if (player.position === 'Defender') targetX = Math.min(88, pos.x + (intensity / 12));
      }

      const jitter = (intensity / 100) * 4;
      return {
        x: pos.x + (targetX - pos.x) * 0.15,
        y: Math.min(90, Math.max(10, pos.y + (Math.random() - 0.5) * jitter))
      };
    }));
  }, [isLive, threatData, homeSquad, awaySquad]);

  // ✅ حركة الكرة تعتمد على منطقة اللعب وشدة الضغط لمحاكاة التمريرات والتسديدات
  useEffect(() => {
    if (!isLive || !threatData) return;
    
    const { userThreat, intensity = 30, actionZone = 'midfield' } = threatData;
    
    let targetX = 50;
    let targetY = 50;

    // تحديد موقع الكرة بناءً على منطقة اللعب مع إضافة عشوائية لمحاكاة التمريرات
    if (actionZone === 'opp_box') { 
      targetX = 85; 
      targetY = 30 + Math.random() * 40; // تتحرك في صندوق الخصم
    } else if (actionZone === 'opp_half') { 
      targetX = 70; 
      targetY = 20 + Math.random() * 60; // نصف ملعب الخصم
    } else if (actionZone === 'own_half') { 
      targetX = 30; 
      targetY = 20 + Math.random() * 60; // نصف ملعبنا
    } else if (actionZone === 'own_box') { 
      targetX = 15; 
      targetY = 30 + Math.random() * 40; // صندوقنا
    }

    // كلما زادت الشدة، تحركت الكرة أسرع وبشكل أكثر حدة (محاكاة للهجمات السريعة)
    const speed = intensity > 70 ? 0.25 : 0.12;
    const chaos = intensity > 70 ? (intensity / 15) : (intensity / 30);

    setBallPos(prev => ({
      x: prev.x + (targetX - prev.x) * speed,
      y: prev.y + (targetY - prev.y) * speed + (Math.random() - 0.5) * chaos
    }));
  }, [isLive, threatData]);

  const getBallColor = () => {
    if (!threatData) return theme.accent;
    if (threatData.userThreat > 55) return theme.homeTeamColor;
    if (threatData.userThreat < 45) return theme.awayTeamColor;
    return '#ffffff';
  };

  const getBallGlow = () => {
    if (!threatData) return theme.accent;
    if (threatData.userThreat > 55) return theme.homeTeamColor;
    if (threatData.userThreat < 45) return theme.awayTeamColor;
    return '#ffffff';
  };

  const ballColor = getBallColor();
  const ballGlow = getBallGlow();

  return (
    <div 
      className="relative w-full h-52 rounded-xl overflow-hidden border-2 shadow-2xl"
      style={{
        background: 'linear-gradient(to bottom, #2d7a3e 0%, #1f5a2b 100%)',
        borderColor: theme.accent,
        boxShadow: `0 0 30px ${theme.glow}`,
      }}
    >
      {threatData?.userThreat > 55 && (
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-yellow-500/20 to-transparent rounded-r-lg" />
      )}
      {threatData?.userThreat < 45 && (
        <div className="absolute left-0 top-0 bottom-0 w-1/3 bg-gradient-to-r from-blue-500/20 to-transparent rounded-l-lg" />
      )}

      <div className="absolute inset-0 border-2 border-white/20 rounded-xl"></div>
      <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-white/30"></div>
      <div className="absolute left-1/2 top-1/2 w-14 h-14 -ml-7 -mt-7 rounded-full border-2 border-white/30"></div>
      <div className="absolute left-0 top-1/4 w-16 h-32 border-2 border-white/30 border-l-0 rounded-r-lg"></div>
      <div className="absolute right-0 top-1/4 w-16 h-32 border-2 border-white/30 border-r-0 rounded-l-lg"></div>
      <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-4 h-16 bg-black/40 border-2 border-white/40 rounded-l-md flex items-center justify-center"><div className="w-2 h-12 bg-white/20 rounded"></div></div>
      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-16 bg-black/40 border-2 border-white/40 rounded-r-md flex items-center justify-center"><div className="w-2 h-12 bg-white/20 rounded"></div></div>

      {homeSquad.filter(p => p.position === 'Goalkeeper').map((gk, idx) => (
        <div 
          key={`gk-home-${idx}`} 
          className="absolute w-8 h-8 rounded-full overflow-hidden border-2 shadow-lg"
          style={{ left: '8%', top: '50%', transform: 'translate(-50%, -50%)', borderColor: theme.homeTeamColor }}
        >
          <img src={getPlayerAvatarSrc(gk)} alt="GK" className="w-full h-full object-cover" onError={e => (e.target.src = '/img/player.jpg')} />
        </div>
      ))}
      {awaySquad.filter(p => p.position === 'Goalkeeper').map((gk, idx) => (
        <div 
          key={`gk-away-${idx}`} 
          className="absolute w-8 h-8 rounded-full overflow-hidden border-2 shadow-lg"
          style={{ left: '92%', top: '50%', transform: 'translate(-50%, -50%)', borderColor: theme.awayTeamColor }}
        >
          <img src={getPlayerAvatarSrc(gk)} alt="GK" className="w-full h-full object-cover" onError={e => (e.target.src = '/img/player.jpg')} />
        </div>
      ))}

      {homeSquad.map((player, i) => {
        if (player.position === 'Goalkeeper') return null;
        const pos = homePlayersPos[i] || getPositionStyle(player.position, 'home', homeSquad, i);
        return (
          <div 
            key={`home-${player.id}`} 
            className="absolute w-7 h-7 rounded-full overflow-hidden shadow-md border border-white"
            style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: 'translate(-50%, -50%)', borderColor: theme.homeTeamColor }}
          >
            <img src={getPlayerAvatarSrc(player)} alt={player.position} className="w-full h-full object-cover" onError={e => (e.target.src = '/img/player.jpg')} />
          </div>
        );
      })}
      {awaySquad.map((player, i) => {
        if (player.position === 'Goalkeeper') return null;
        const pos = awayPlayersPos[i] || getPositionStyle(player.position, 'away', awaySquad, i);
        return (
          <div 
            key={`away-${player.id}`} 
            className="absolute w-7 h-7 rounded-full overflow-hidden shadow-md border border-white"
            style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: 'translate(-50%, -50%)', borderColor: theme.awayTeamColor }}
          >
            <img src={getPlayerAvatarSrc(player)} alt={player.position} className="w-full h-full object-cover" onError={e => (e.target.src = '/img/player.jpg')} />
          </div>
        );
      })}

      <div 
        className="absolute w-3 h-3 rounded-full shadow-md transition-all duration-300"
        style={{ 
          left: `${ballPos.x}%`, 
          top: `${ballPos.y}%`, 
          transform: 'translate(-50%, -50%)',
          background: ballColor,
          boxShadow: `0 0 15px ${ballGlow}80`,
          transition: 'background 0.5s ease, box-shadow 0.5s ease',
        }} 
      />
    </div>
  );
};

const Toast = ({ message, type, onClose, theme }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : type === 'warning' ? 'bg-yellow-600' : 'bg-blue-600';
  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 ${bgColor} text-white px-6 py-3 rounded-xl shadow-2xl backdrop-blur-sm border border-white/20 flex items-center gap-3`}
      style={{ borderColor: theme.accent, boxShadow: `0 0 20px ${theme.glow}` }}
    >
      <span>{type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}</span>
      <span className="font-medium">{message}</span>
    </motion.div>
  );
};

// ========== Substitution Pitch (PRO UI) ==========
const SubstitutionPitch = ({ activeSquad, benchSquad, onSwap, selectedOut, setSelectedOut, t, theme }) => {
  const { i18n } = useTranslation();
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getPositionsForSquad = useCallback((squad) => {
    const groups = { Goalkeeper: [], Defender: [], Midfielder: [], Striker: [] };
    squad.forEach(p => {
      if (groups[p.position]) groups[p.position].push(p);
    });

    const coords = [];

    if (!isMobile) {
      const pitchWidth = 80;
      const margin = 10;
      const verticalOffset = 55;

      const distributeVertical = (players, xPos) => {
        const count = players.length;
        if (count === 0) return;
        if (count === 1) {
          coords.push({ player: players[0], x: xPos, y: verticalOffset });
        } else {
          const step = (pitchWidth - 2 * margin) / (count - 1);
          players.forEach((p, i) => {
            const y = verticalOffset + (i - (count - 1) / 2) * step;
            coords.push({ player: p, x: xPos, y });
          });
        }
      };

      if (groups.Goalkeeper.length) coords.push({ player: groups.Goalkeeper[0], x: 8, y: verticalOffset });
      distributeVertical(groups.Defender, 28);
      distributeVertical(groups.Midfielder, 50);
      distributeVertical(groups.Striker, 78);
    } else {
      const pitchHeight = 80;
      const margin = 10;
      const horizontalOffset = 45;

      const distributeHorizontal = (players, yPos) => {
        const count = players.length;
        if (count === 0) return;
        if (count === 1) {
          coords.push({ player: players[0], x: horizontalOffset, y: yPos });
        } else {
          const step = (pitchHeight - 2 * margin) / (count - 1);
          players.forEach((p, i) => {
            const x = horizontalOffset + (i - (count - 1) / 2) * step;
            coords.push({ player: p, x, y: yPos });
          });
        }
      };

      if (groups.Goalkeeper.length) coords.push({ player: groups.Goalkeeper[0], x: horizontalOffset, y: 8 });
      distributeHorizontal(groups.Defender, 28);
      distributeHorizontal(groups.Midfielder, 50);
      distributeHorizontal(groups.Striker, 78);
    }

    return coords;
  }, [isMobile]);

  const positionedPlayers = useMemo(() => getPositionsForSquad(activeSquad), [activeSquad, getPositionsForSquad]);

  const [dragSource, setDragSource] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const handleDragStart = useCallback((e, playerId, type) => {
    setDragSource({ type, playerId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type, playerId }));
    setTimeout(() => {
      e.target.style.opacity = '0.2';
      e.target.style.filter = 'blur(4px)';
      e.target.style.transform = 'translate(-50%, -50%) scale(0.8)';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    e.target.style.filter = 'none';
    e.target.style.transform = 'translate(-50%, -50%) scale(1)';
    setDragSource(null);
    setDropTarget(null);
  }, []);

  const handleDragOver = useCallback((e, type, playerId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragSource && dragSource.type !== type && dragSource.playerId !== playerId) {
      setDropTarget({ type, playerId });
    } else {
      setDropTarget(null);
    }
  }, [dragSource]);

  const handleDrop = useCallback((e, type, playerId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragSource || dragSource.type === type || dragSource.playerId === playerId) {
      setDragSource(null);
      setDropTarget(null);
      return;
    }
    const activeId = dragSource.type === 'active' ? dragSource.playerId : playerId;
    const benchId = dragSource.type === 'bench' ? dragSource.playerId : playerId;
    onSwap(activeId, benchId);
    setDragSource(null);
    setDropTarget(null);
  }, [dragSource, onSwap]);

  const getPlayerImage = useCallback((position, rarity) => {
    const map = { Goalkeeper: 'goalkeeper', Defender: 'defender', Midfielder: 'midfielder', Striker: 'striker' };
    const base = map[position] || 'player';
    if (rarity === 'gold' || rarity === 'silver' || rarity === 'bronze') return `/img/${rarity}_${base}.jpg`;
    return `/img/${base}.jpg`;
  }, []);

  const getRarityGradient = (rarity) => {
    if (rarity === 'gold') return 'linear-gradient(135deg, #b8860b 0%, #ffd700 50%, #b8860b 100%)';
    if (rarity === 'silver') return 'linear-gradient(135deg, #757575 0%, #c0c0c0 50%, #757575 100%)';
    if (rarity === 'bronze') return 'linear-gradient(135deg, #8b4513 0%, #cd7f32 50%, #8b4513 100%)';
    return theme.popupBg;
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      {/* Pitch Container with Grass Pattern */}
      <div 
        className="relative w-full" 
        style={{ aspectRatio: isMobile ? '9/16' : '16/9' }}
      >
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border-2"
          style={{ 
            background: `
              repeating-linear-gradient(
                0deg,
                rgba(255,255,255,0.03) 0px,
                rgba(255,255,255,0.03) 40px,
                transparent 40px,
                transparent 80px
              ),
              linear-gradient(180deg, #2d8a4e 0%, #1e6b38 50%, #2d8a4e 100%)
            `,
            borderColor: theme.accent,
            boxShadow: `0 0 30px ${theme.glow}, inset 0 0 50px rgba(0,0,0,0.3)`
          }}
        >
          {!isMobile ? (
            <>
              <div className="absolute inset-0 border-2 border-white/40 rounded-sm" />
              <div className="absolute left-1/2 top-0 bottom-0 w-[1.5px] bg-white/40" />
              <div className="absolute left-1/2 top-1/2 w-[14%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-white/40" />
              <div className="absolute left-1/2 top-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/60" />
              <div className="absolute left-[3%] top-1/2 -translate-y-1/2 w-[14%] h-[40%] border-[1.5px] border-white/40 border-l-0 rounded-r-sm" />
              <div className="absolute right-[3%] top-1/2 -translate-y-1/2 w-[14%] h-[40%] border-[1.5px] border-white/40 border-r-0 rounded-l-sm" />
              <div className="absolute left-[3%] top-1/2 -translate-y-1/2 w-[6%] h-[22%] border-[1.5px] border-white/40 border-l-0 rounded-r-sm" />
              <div className="absolute right-[3%] top-1/2 -translate-y-1/2 w-[6%] h-[22%] border-[1.5px] border-white/40 border-r-0 rounded-l-sm" />
              <div className="absolute left-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-r-[1.5px] border-white/40 rounded-tr-full" />
              <div className="absolute left-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-r-[1.5px] border-white/40 rounded-br-full" />
              <div className="absolute right-[3%] top-[3%] w-[4%] aspect-square border-t-[1.5px] border-l-[1.5px] border-white/40 rounded-tl-full" />
              <div className="absolute right-[3%] bottom-[3%] w-[4%] aspect-square border-b-[1.5px] border-l-[1.5px] border-white/40 rounded-bl-full" />
              <div className="absolute left-[17%] top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
              <div className="absolute right-[17%] top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 border-2 border-white/40 rounded-sm" />
              <div className="absolute top-1/2 left-0 right-0 h-[1.5px] bg-white/40" />
              <div className="absolute top-1/2 left-1/2 w-[25%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-white/40" />
              <div className="absolute top-1/2 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/60" />
              <div className="absolute top-[3%] left-1/2 -translate-x-1/2 w-[40%] h-[14%] border-[1.5px] border-white/40 border-t-0 rounded-b-sm" />
              <div className="absolute bottom-[3%] left-1/2 -translate-x-1/2 w-[40%] h-[14%] border-[1.5px] border-white/40 border-b-0 rounded-t-sm" />
              <div className="absolute top-[3%] left-1/2 -translate-x-1/2 w-[22%] h-[6%] border-[1.5px] border-white/40 border-t-0 rounded-b-sm" />
              <div className="absolute bottom-[3%] left-1/2 -translate-x-1/2 w-[22%] h-[6%] border-[1.5px] border-white/40 border-b-0 rounded-t-sm" />
              <div className="absolute top-[3%] left-[3%] w-[4%] aspect-square border-t-[1.5px] border-b-[1.5px] border-white/40 rounded-br-full" />
              <div className="absolute top-[3%] right-[3%] w-[4%] aspect-square border-t-[1.5px] border-b-[1.5px] border-white/40 rounded-bl-full" />
              <div className="absolute bottom-[3%] left-[3%] w-[4%] aspect-square border-t-[1.5px] border-b-[1.5px] border-white/40 rounded-tr-full" />
              <div className="absolute bottom-[3%] right-[3%] w-[4%] aspect-square border-t-[1.5px] border-b-[1.5px] border-white/40 rounded-tl-full" />
              <div className="absolute top-[17%] left-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
              <div className="absolute bottom-[17%] left-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
            </>
          )}

          {positionedPlayers.map((item) => {
            const player = item.player;
            const isDragging = dragSource && dragSource.type === 'active' && dragSource.playerId === player.id;
            const isDropTarget = dropTarget && dropTarget.type === 'active' && dropTarget.playerId === player.id;
            const isSelectedOut = selectedOut && selectedOut.id === player.id;
            const power = player.playerPower || Math.round(calculatePlayerPower(player));

            return (
              <div
                key={player.id}
                draggable
                onDragStart={(e) => handleDragStart(e, player.id, 'active')}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, 'active', player.id)}
                onDrop={(e) => handleDrop(e, 'active', player.id)}
                onClick={() => setSelectedPlayer(player)}
                className="absolute z-10 flex flex-col items-center transition-all duration-300 group cursor-grab active:cursor-grabbing"
                style={{
                  left: `${item.x}%`,
                  top: `${item.y}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {/* Name Tag */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold px-2 py-0.5 rounded bg-black/70 backdrop-blur-sm whitespace-nowrap shadow-md border border-white/10 z-20 group-hover:scale-110 transition-transform">
                  {player.name.length > 10 ? player.name.slice(0,8)+'..' : player.name}
                </div>

                <div className="relative">
                  {/* Glow Ring */}
                  <div 
                    className={`absolute inset-0 rounded-full blur-md transition-opacity ${isDropTarget ? 'opacity-100 animate-pulse bg-white' : 'opacity-40 group-hover:opacity-70'}`}
                    style={{ background: isDropTarget ? '#fff' : theme.accent }}
                  />
                  
                  {/* Player Image */}
                  <div 
                    className={`relative w-14 h-14 rounded-full overflow-hidden border-2 shadow-xl transition-all ${isDragging ? 'scale-90 blur-sm' : ''} ${isDropTarget ? 'scale-110 ring-2 ring-white' : ''} ${isSelectedOut ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-transparent' : ''}`}
                    style={{ borderColor: theme.accent, background: '#111' }}
                  >
                    <img
                      src={getPlayerImage(player.position, player.rarity)}
                      alt={player.name}
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.src = '/img/player.jpg'}
                    />
                  </div>

                  {/* Power Badge */}
                  <div 
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-md text-[9px] font-black text-white shadow-lg border border-white/20"
                    style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                  >
                    {power}
                  </div>

                  {/* OUT Badge */}
                  {isSelectedOut && (
                    <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1 rounded-full font-bold shadow-lg animate-pulse z-30">OUT</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bench Area (Dugout) */}
      <div 
        className="mt-6 p-4 rounded-2xl border backdrop-blur-md relative overflow-hidden"
        style={{ 
          background: 'rgba(15, 23, 42, 0.85)', 
          borderColor: theme.cardBorder, 
          boxShadow: `inset 0 2px 15px rgba(0,0,0,0.5), 0 0 25px ${theme.glow}` 
        }}
      >
        {/* Bench Header */}
        <div className="flex items-center gap-2 mb-3 border-b border-white/10 pb-2">
          <span className="text-lg">🪑</span>
          <h3 className="text-sm font-bold tracking-wide uppercase" style={{ color: theme.textSecondary }}>
            {t('match.bench') || 'البدلاء'}
          </h3>
        </div>

        {/* Bench Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
          {benchSquad.map((player) => {
            const isDragging = dragSource && dragSource.type === 'bench' && dragSource.playerId === player.id;
            const isDropTarget = dropTarget && dropTarget.type === 'bench' && dropTarget.playerId === player.id;
            const power = player.playerPower || Math.round(calculatePlayerPower(player));

            return (
              <div
                key={player.id}
                draggable
                onDragStart={(e) => handleDragStart(e, player.id, 'bench')}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, 'bench', player.id)}
                onDrop={(e) => handleDrop(e, 'bench', player.id)}
                onClick={() => setSelectedPlayer(player)}
                className={`relative p-2 rounded-xl border text-center cursor-grab transition-all active:scale-95 hover:scale-105 hover:border-white/50 group ${isDragging ? 'opacity-30 scale-90 blur-sm' : ''} ${isDropTarget ? 'ring-2 ring-offset-2 ring-offset-transparent scale-105' : ''}`}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderColor: isDropTarget ? theme.accent : 'rgba(255,255,255,0.1)',
                  boxShadow: isDropTarget ? `0 0 15px ${theme.glow}` : 'none'
                }}
              >
                <div className="w-12 h-12 rounded-full overflow-hidden mx-auto border-2 mb-1 shadow-lg group-hover:scale-110 transition-transform" style={{ borderColor: theme.accent }}>
                  <img
                    src={getPlayerImage(player.position, player.rarity)}
                    alt={player.name}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src = '/img/player.jpg'}
                  />
                </div>
                <div className="text-[11px] font-bold text-white truncate">{player.name}</div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <span className="text-[9px] text-gray-400">{player.position}</span>
                  <span className="text-[9px] font-bold" style={{ color: theme.textSecondary }}>⚡{power}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Player Stats Modal (Premium Card Style) */}
      {selectedPlayer && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" 
          onClick={() => setSelectedPlayer(null)}
        >
          <div 
            className="rounded-2xl max-w-sm w-full p-6 border shadow-2xl relative overflow-hidden"
            style={{ 
              background: getRarityGradient(selectedPlayer.rarity), 
              borderColor: selectedPlayer.rarity === 'gold' ? '#ffd700' : theme.popupBorder,
              boxShadow: `0 0 40px ${theme.glow}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Pattern Overlay */}
            <div 
              className="absolute inset-0 opacity-10" 
              style={{ 
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3C/g%3E%3C/svg%3E")` 
              }} 
            />
            
            <div className="relative z-10">
              <button 
                onClick={() => setSelectedPlayer(null)} 
                className="float-right text-white/80 hover:text-white transition text-xl font-bold"
              >
                ✕
              </button>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 shadow-xl" style={{ borderColor: '#fff' }}>
                  <img
                    src={getPlayerImage(selectedPlayer.position, selectedPlayer.rarity)}
                    alt={selectedPlayer.name}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src = '/img/player.jpg'}
                  />
                </div>
                <div>
                  <h3 className="text-xl font-black text-white drop-shadow-md">{selectedPlayer.name}</h3>
                  <p className="text-white/80 text-sm font-semibold">{selectedPlayer.position}</p>
                  {selectedPlayer.special_ability && (
                    <p className="text-white text-sm font-bold mt-1">✨ {selectedPlayer.special_ability}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                {[
                  { label: 'ATK', val: selectedPlayer.attack, color: 'text-blue-300' },
                  { label: 'DEF', val: selectedPlayer.defense, color: 'text-green-300' },
                  { label: 'SPD', val: selectedPlayer.speed, color: 'text-yellow-300' },
                  { label: 'ACC', val: selectedPlayer.accuracy, color: 'text-purple-300' },
                  { label: 'STR', val: selectedPlayer.strength || 50, color: 'text-red-300' },
                  { label: 'TAC', val: selectedPlayer.tactics || 50, color: 'text-cyan-300' }
                ].map(stat => (
                  <div key={stat.label} className="rounded-lg p-2 text-center bg-black/30 backdrop-blur-sm border border-white/10">
                    <div className="text-[10px] text-white/70 font-bold">{stat.label}</div>
                    <div className={`font-black text-lg ${stat.color} drop-shadow-md`}>{stat.val}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== Main LiveMatch Component ==========
const LiveMatch = ({ matchData: initialMatchData, userId, onFinish }) => {
  const { t, i18n } = useTranslation();
  const socket = useSocket()?.socket;
  const { playGoal, playWhistle, playRed, playAmbulance, startCheer, stopCheerSound } = useSoundEffects();

  const tournamentType = initialMatchData.tournamentType || 
                         getTournamentTypeFromCompetition(initialMatchData.competition);
  const theme = useMemo(() => {
    const selectedTheme = getTournamentTheme(tournamentType);
    return selectedTheme;
  }, [tournamentType]);
  
  const tournamentName = initialMatchData.tournamentName || 
                         getTournamentNameFromCompetition(initialMatchData.competition, initialMatchData.continent);

  const homeUserId = initialMatchData.homeUserId || userId;
  const awayUserId = initialMatchData.awayUserId || initialMatchData.opponentId;
  const isHomeTeam = userId === homeUserId;
  const opponentId = initialMatchData.opponentId;

  const getInitialScore = () => {
    if (initialMatchData.score) {
      const userScore = initialMatchData.score[userId] ?? 0;
      const oppScore = initialMatchData.score[initialMatchData.opponentId] ?? 0;
      return { home: userScore, away: oppScore };
    }
    return { home: 0, away: 0 };
  };

  const [phase, setPhase] = useState(initialMatchData.phase || 'first_half');
  const [phaseTimer, setPhaseTimer] = useState(initialMatchData.phaseTimeRemaining ?? 60);
  const [half, setHalf] = useState(initialMatchData.half || 1);
  const [events, setEvents] = useState(initialMatchData.events || []);
  const [score, setScore] = useState(getInitialScore);
  const [halfTimeScore, setHalfTimeScore] = useState(null);
  const [matchFinished, setMatchFinished] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [cardChanges, setCardChanges] = useState([]);
  const [yourSquadRaw, setYourSquadRaw] = useState(initialMatchData.yourSquad || []);
  const [yourBench, setYourBench] = useState(initialMatchData.yourBench || []);
  const [opponentSquad, setOpponentSquad] = useState(initialMatchData.opponentSquad || []);
  const [opponentBench, setOpponentBench] = useState(initialMatchData.opponentBench || []);
  const [redCardedPlayerIds, setRedCardedPlayerIds] = useState(initialMatchData.redCardedPlayerIds || []);
  const [injuredPlayerIds, setInjuredPlayerIds] = useState(initialMatchData.injuredPlayerIds || []);
  const [highlightedPlayer, setHighlightedPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState({});
  const [goalScreen, setGoalScreen] = useState(null);
  const [toast, setToast] = useState(null);
  const [teamStrengths, setTeamStrengths] = useState({ home: 0, away: 0 });
  const [opponentLeft, setOpponentLeft] = useState(false);
  const [myFormation, setMyFormation] = useState(initialMatchData.formation || 'traditionalAttack');
  const [selectedFormation, setSelectedFormation] = useState(myFormation);
  const [myPlayingStyle, setMyPlayingStyle] = useState(initialMatchData.playingStyle || 'balanced');
  const [selectedPlayingStyle, setSelectedPlayingStyle] = useState(myPlayingStyle);
  const [showHalfTimeAnimation, setShowHalfTimeAnimation] = useState(false);
  const halfTimeAnimationTimer = useRef(null);
  const eventsEndRef = useRef(null);
  const [timerFrozen, setTimerFrozen] = useState(false);

  const [showSubs, setShowSubs] = useState(false);
  const [selectedOut, setSelectedOut] = useState(null);
  const [substitutions, setSubstitutions] = useState([]);
  const [hasUnsavedSubs, setHasUnsavedSubs] = useState(false);

  const [showAmbulance, setShowAmbulance] = useState(false);
  const [ambulanceData, setAmbulanceData] = useState(null);

  // ✅ حالة مؤشر التهديد (Threat Data)
  const [threatData, setThreatData] = useState({ userThreat: 50, attackingTeam: 'neutral' });

  const hasInitializedRef = useRef(false);
  const isResumingRef = useRef(false);
  const lastTimerValueRef = useRef(phaseTimer);
  const stuckCheckIntervalRef = useRef(null);

  const [offsidePopup, setOffsidePopup] = useState(null);
  const offsideTimerRef = useRef(null);
  const lastEventRef = useRef(null);

  const yourSquad = yourSquadRaw.filter(p => !redCardedPlayerIds.includes(p.id));

  const enrichSquadWithPower = useCallback(squad => squad.map(p => ({ ...p, playerPower: Math.round(calculatePlayerPower(p)) })), []);

  const getStoredUsername = () => localStorage.getItem('username') || '';
  const yourTeamDisplayName = useCallback(() => `${t('match.you')} (${getStoredUsername()})`, [t]);
  const getOpponentDisplayName = useCallback(() => {
    const username = initialMatchData.opponentUsername || initialMatchData.opponentTeamName;
    if (username) return `${t('match.opponent')} (${username})`;
    return t('match.opponent');
  }, [initialMatchData, t]);

  const [yourTeamName, setYourTeamName] = useState(yourTeamDisplayName());
  const [opponentTeamName, setOpponentTeamName] = useState(getOpponentDisplayName());

  useEffect(() => {
    setYourTeamName(yourTeamDisplayName());
    setOpponentTeamName(getOpponentDisplayName());
  }, [yourTeamDisplayName, getOpponentDisplayName]);

  useEffect(() => {
    if (!matchFinished && initialMatchData?.matchId) {
      if (!hasInitializedRef.current && !isResumingRef.current) {
        hasInitializedRef.current = true;
        playWhistle();
        startCheer();
      } else if (isResumingRef.current) {
        isResumingRef.current = false;
      }
    }
    return () => {
      if (!matchFinished) {
        stopCheerSound();
      }
    };
  }, [initialMatchData?.matchId, matchFinished, playWhistle, startCheer, stopCheerSound]);

  useEffect(() => {
    if (!socket) return;
    const onResumeMatch = () => {
      isResumingRef.current = true;
    };
    socket.on('resume-match', onResumeMatch);
    return () => {
      socket.off('resume-match', onResumeMatch);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || matchFinished) return;
    lastTimerValueRef.current = phaseTimer;
    if (stuckCheckIntervalRef.current) clearInterval(stuckCheckIntervalRef.current);
    stuckCheckIntervalRef.current = setInterval(() => {
      if (phaseTimer === lastTimerValueRef.current && phaseTimer > 0 && socket.connected) {
        socket.emit('get-match-state');
      }
      lastTimerValueRef.current = phaseTimer;
    }, 3000);
    return () => {
      if (stuckCheckIntervalRef.current) clearInterval(stuckCheckIntervalRef.current);
    };
  }, [phaseTimer, socket, matchFinished]);

  useEffect(() => {
    const newStats = {};
    events.forEach(ev => {
      if (ev.playerId) {
        if (!newStats[ev.playerId]) newStats[ev.playerId] = { goals: 0, yellow: 0, red: 0, injury: 0 };
        if (ev.type === 'goal') newStats[ev.playerId].goals++;
        else if (ev.type === 'yellow') newStats[ev.playerId].yellow++;
        else if (ev.type === 'red') newStats[ev.playerId].red++;
        else if (ev.type === 'injury') newStats[ev.playerId].injury++;
      }
    });
    setPlayerStats(newStats);
  }, [events]);

  useEffect(() => {
    return () => {
      if (halfTimeAnimationTimer.current) clearTimeout(halfTimeAnimationTimer.current);
    };
  }, []);

  useEffect(() => {
    if (showAmbulance) {
      const timer = setTimeout(() => {
        setShowAmbulance(false);
        setAmbulanceData(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showAmbulance]);

  useEffect(() => {
    const handleOnline = () => {
      setToast({ message: 'Internet restored, reconnecting...', type: 'success' });
      setTimeout(() => {
        if (socket && !socket.connected) {
          socket.connect();
          setTimeout(() => socket.emit('rejoin-match'), 500);
        } else if (socket && socket.connected) {
          socket.emit('rejoin-match');
        }
      }, 500);
      setTimeout(() => setToast(null), 3000);
    };
    const handleOffline = () => {
      setToast({ message: 'No internet connection', type: 'warning' });
      setTimeout(() => setToast(null), 3000);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || matchFinished) return;
    const healthCheck = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit('get-match-state');
      } else if (socket && !socket.connected && navigator.onLine) {
        socket.connect();
        setTimeout(() => socket.emit('rejoin-match'), 500);
      }
    }, 5000);
    return () => clearInterval(healthCheck);
  }, [socket, matchFinished]);

  useEffect(() => {
    if (!socket) return;
    const handleSubsUpdated = (data) => {
      setYourSquadRaw(enrichSquadWithPower(data.squad));
      setYourBench(enrichSquadWithPower(data.bench));
      if (data.formation) { setMyFormation(data.formation); setSelectedFormation(data.formation); }
      setHasUnsavedSubs(false);
      setSubstitutions([]);
      setSelectedOut(null);
    };
    const handleFormationUpdated = (data) => {
      if (data.formation) { setMyFormation(data.formation); setSelectedFormation(data.formation); }
    };
    const handlePlayingStyleUpdated = (data) => {
      if (data.playingStyle) {
        setMyPlayingStyle(data.playingStyle);
        setSelectedPlayingStyle(data.playingStyle);
      }
    };
    socket.on('subs-updated', handleSubsUpdated);
    socket.on('formation-updated', handleFormationUpdated);
    socket.on('playing-style-updated', handlePlayingStyleUpdated);
    return () => {
      socket.off('subs-updated', handleSubsUpdated);
      socket.off('formation-updated', handleFormationUpdated);
      socket.off('playing-style-updated', handlePlayingStyleUpdated);
    };
  }, [socket, enrichSquadWithPower]);

  const sendSubsToServer = useCallback((activeIds, benchIds, formation) => {
    if (!socket || phase !== 'substitutions') return;
    socket.emit('update-subs', { matchId: initialMatchData.matchId, newActiveIds: activeIds, newBenchIds: benchIds, formation: formation || myFormation });
  }, [socket, phase, initialMatchData.matchId, myFormation]);

  const sendFormationToServer = useCallback((formation) => {
    if (!socket || phase !== 'substitutions') return;
    socket.emit('change-formation', { matchId: initialMatchData.matchId, formation });
  }, [socket, phase, initialMatchData.matchId]);

  const sendPlayingStyleToServer = useCallback((playingStyle) => {
    if (!socket || phase !== 'substitutions') return;
    socket.emit('change-playing-style', { matchId: initialMatchData.matchId, playingStyle });
  }, [socket, phase, initialMatchData.matchId]);

  useEffect(() => {
    setShowSubs(phase === 'substitutions' && !matchFinished);
  }, [phase, matchFinished]);

  const handleFinalSquad = useCallback((data) => {
    setYourSquadRaw(enrichSquadWithPower(data.squad));
    setYourBench(enrichSquadWithPower(data.bench));
    if (data.formation) { setMyFormation(data.formation); setSelectedFormation(data.formation); }
    setHasUnsavedSubs(false);
    setSubstitutions([]);
  }, [enrichSquadWithPower]);

  const closeAmbulance = useCallback(() => {
    setShowAmbulance(false);
    setAmbulanceData(null);
  }, []);

  const handleResumeMatch = useCallback((data) => {
    setShowAmbulance(false);
    setAmbulanceData(null);
    setOpponentLeft(false);
    setPhase(data.phase);
    setPhaseTimer(data.phaseTimeRemaining);
    setHalf(data.half);
    const userScore = data.score[userId] ?? 0;
    const oppScore = data.score[opponentId] ?? 0;
    setScore({ home: userScore, away: oppScore });
    if (!hasUnsavedSubs) {
      setYourSquadRaw(enrichSquadWithPower(data.yourSquad || []));
      setYourBench(enrichSquadWithPower(data.yourBench || []));
    }
    setOpponentSquad(enrichSquadWithPower(data.opponentSquad || []));
    setOpponentBench(enrichSquadWithPower(data.opponentBench || []));
    setEvents(data.events.map(ev => ({ ...ev, isNew: false })));
    setMyFormation(data.formation);
    setSelectedFormation(data.formation);
    setMyPlayingStyle(data.playingStyle || 'balanced');
    setSelectedPlayingStyle(data.playingStyle || 'balanced');
    const newRedCarded = [];
    const newInjured = [];
    data.events.forEach(ev => {
      if (ev.type === 'red' && ev.playerId && !newRedCarded.includes(ev.playerId)) newRedCarded.push(ev.playerId);
      if (ev.type === 'injury' && ev.playerId && !newInjured.includes(ev.playerId)) newInjured.push(ev.playerId);
    });
    setRedCardedPlayerIds(newRedCarded);
    setInjuredPlayerIds(newInjured);
    if (data.opponentUsername) setOpponentTeamName(`${t('match.opponent')} (${data.opponentUsername})`);
    const newStats = {};
    data.events.forEach(ev => {
      if (ev.playerId) {
        if (!newStats[ev.playerId]) newStats[ev.playerId] = { goals: 0, yellow: 0, red: 0, injury: 0 };
        if (ev.type === 'goal') newStats[ev.playerId].goals++;
        else if (ev.type === 'yellow') newStats[ev.playerId].yellow++;
        else if (ev.type === 'red') newStats[ev.playerId].red++;
        else if (ev.type === 'injury') newStats[ev.playerId].injury++;
      }
    });
    setPlayerStats(newStats);
    setShowHalfTimeAnimation(false);
    setShowSubs(false);
    if (halfTimeAnimationTimer.current) clearTimeout(halfTimeAnimationTimer.current);
    if (data.phase === 'substitutions') {
      setShowSubs(true);
      setHalfTimeScore({ [homeUserId]: userScore, [awayUserId]: oppScore });
    } else if (data.phase === 'half_time_animation') {
      setShowHalfTimeAnimation(true);
      halfTimeAnimationTimer.current = setTimeout(() => {
        setShowHalfTimeAnimation(false);
        setShowSubs(true);
        setHalfTimeScore({ [homeUserId]: userScore, [awayUserId]: oppScore });
      }, 3000);
    }
  }, [userId, opponentId, homeUserId, awayUserId, t, enrichSquadWithPower, hasUnsavedSubs]);

  const handleServerShutdown = useCallback((data) => {
    setToast({ message: t('match.serverShutdown', { defaultValue: '⚠️ Server is restarting. Match will resume automatically.' }), type: 'error' });
  }, [t]);

  // ✅ استقبال حدث match-threat من الخادم
  useEffect(() => {
    if (!socket) return;
    const handleMatchThreat = (data) => {
      setThreatData(data);
    };
    socket.on('match-threat', handleMatchThreat);
    return () => {
      socket.off('match-threat', handleMatchThreat);
    };
  }, [socket]);

  // ========== UseEffect for socket events with goalScreen and offsidePopup dependencies ==========
  useEffect(() => {
    if (!socket) return;
    const handlePhaseChange = (data) => {
      const newPhase = data.phase;
      setPhase(newPhase);
      setShowHalfTimeAnimation(false);
      setShowSubs(false);
      if (halfTimeAnimationTimer.current) clearTimeout(halfTimeAnimationTimer.current);
      if (newPhase === 'half_time_animation') {
        setShowHalfTimeAnimation(true);
        halfTimeAnimationTimer.current = setTimeout(() => setShowHalfTimeAnimation(false), 3000);
      } else if (newPhase === 'substitutions') setShowSubs(true);
      else if (newPhase === 'first_half') setHalf(1);
      else if (newPhase === 'second_half') setHalf(2);
    };
    const handlePhaseTimer = ({ seconds }) => { if (!timerFrozen) setPhaseTimer(seconds); };
    const handleMatchEvent = ({ event }) => {
      //console.log('📢 [LiveMatch] Received match-event:', event);
      
      setEvents(prev => [...prev, { ...event, isNew: true }]);

      // ✅ منع تراكم GoalScreen
      if (event.type === 'goal') {
        if (goalScreen) return; // تجاهل الهدف الجديد إذا كان هناك هدف معروض
        playGoal();
        const myScore = event.score?.[userId] ?? 0;
        const oppScore = event.score?.[opponentId] ?? 0;
        setScore({ home: myScore, away: oppScore });
        const isMyTeamGoal = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam);
        const scorerName = event.player || '';
        setGoalScreen({
          playerName: `⚽ ${isMyTeamGoal ? t('match.you') : t('match.opponent')} ${t('match.goal')}${scorerName ? ` - ${scorerName}` : ''}`,
          position: '', rarity: 'common', team: isMyTeamGoal ? 'home' : 'away', score: { home: myScore, away: oppScore }
        });
        setTimeout(() => setGoalScreen(null), 2200);
      }
      else if (event.type === 'greatSave') {
        const isMyTeam = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam);
        let message = '';
        if (event.player) message = t('match.greatSavePlayer', { player: event.player });
        else if (isMyTeam) message = t('match.greatSaveForYou');
        else message = t('match.greatSaveForOpponent');
        setToast({ message: `🧤 ${message}`, type: 'success' });
        setTimeout(() => setToast(null), 2500);
      }
      else if (event.type === 'postHit') {
        const isMyTeam = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam);
        let message = '';
        if (event.player) message = t('match.postHitPlayer', { player: event.player });
        else if (isMyTeam) message = t('match.postHitForYou');
        else message = t('match.postHitForOpponent');
        setToast({ message: `🎯 ${message}`, type: 'warning' });
        setTimeout(() => setToast(null), 2500);
      }
      else if (event.type === 'red' && event.playerId && !redCardedPlayerIds.includes(event.playerId)) {
        playRed();
        setRedCardedPlayerIds(prev => [...prev, event.playerId]);
      }
      else if (event.type === 'injury' && event.playerId && !injuredPlayerIds.includes(event.playerId)) {
        setInjuredPlayerIds(prev => [...prev, event.playerId]);
        playAmbulance();
        setAmbulanceData({ player: event.player || '', team: event.team });
        setShowAmbulance(true);
      }
      else if (event.type === 'substitution') {
        const isMySubstitution = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam);
        if (isMySubstitution) {
          setYourSquadRaw(prev => {
            const outIndex = prev.findIndex(p => p.id === event.playerOutId);
            const inPlayer = yourBench.find(p => p.id === event.playerInId);
            if (outIndex !== -1 && inPlayer) { const newSquad = [...prev]; newSquad[outIndex] = inPlayer; return newSquad; }
            return prev;
          });
          setYourBench(prev => {
            const inIndex = prev.findIndex(p => p.id === event.playerInId);
            if (inIndex !== -1) {
              const newBench = prev.filter(p => p.id !== event.playerInId);
              const outPlayer = yourSquadRaw.find(p => p.id === event.playerOutId);
              if (outPlayer) newBench.push(outPlayer);
              return newBench;
            }
            return prev;
          });
        } else {
          setOpponentSquad(prev => {
            const outIndex = prev.findIndex(p => p.id === event.playerOutId);
            const inPlayer = opponentBench.find(p => p.id === event.playerInId);
            if (outIndex !== -1 && inPlayer) { const newSquad = [...prev]; newSquad[outIndex] = inPlayer; return newSquad; }
            return prev;
          });
          setOpponentBench(prev => {
            const inIndex = prev.findIndex(p => p.id === event.playerInId);
            if (inIndex !== -1) {
              const newBench = prev.filter(p => p.id !== event.playerInId);
              const outPlayer = opponentSquad.find(p => p.id === event.playerOutId);
              if (outPlayer) newBench.push(outPlayer);
              return newBench;
            }
            return prev;
          });
        }
      }
      else if (event.type === 'missed_chance' || event.type === 'offside') {
        // ✅ منع تراكم offsidePopup
        if (offsidePopup) return;
        const eventKey = `${event.second}-${event.type}-${event.team}-${event.playerId || 'null'}`;
        if (lastEventRef.current === eventKey) return;
        lastEventRef.current = eventKey;
        setTimeout(() => { lastEventRef.current = null; }, 5000);

        if (event.type === 'missed_chance') {
          const isMyTeam = (event.team === 'home' && isHomeTeam) || (event.team === 'away' && !isHomeTeam);
          let message = '';
          if (event.player) message = t('match.missedChancePlayer', { player: event.player });
          else if (isMyTeam) message = t('match.missedChanceForYou');
          else message = t('match.missedChanceForOpponent');
          setToast({ message: `😱 ${message}`, type: 'warning' });
          setTimeout(() => setToast(null), 3000);
        } else if (event.type === 'offside') {
          if (offsideTimerRef.current) {
            clearTimeout(offsideTimerRef.current);
            offsideTimerRef.current = null;
          }
          setOffsidePopup({
            type: 'offside',
            team: event.team,
            player: event.player || null,
          });
          offsideTimerRef.current = setTimeout(() => {
            setOffsidePopup(null);
            offsideTimerRef.current = null;
          }, 3000);
          playWhistle();
        }
      }

      if (event.playerId) {
        const allYourPlayers = [...yourSquadRaw, ...yourBench];
        const isHome = allYourPlayers.some(p => p.id === event.playerId);
        setHighlightedPlayer({ id: event.playerId, team: isHome ? 'home' : 'away' });
        setTimeout(() => setHighlightedPlayer(null), 3000);
        setPlayerStats(prev => {
          const ns = { ...prev };
          if (!ns[event.playerId]) ns[event.playerId] = { goals: 0, yellow: 0, red: 0, injury: 0 };
          if (event.type === 'goal') ns[event.playerId].goals++;
          else if (event.type === 'yellow') ns[event.playerId].yellow++;
          else if (event.type === 'red') ns[event.playerId].red++;
          else if (event.type === 'injury') ns[event.playerId].injury++;
          return ns;
        });
      }
      setTimeout(() => setEvents(prev => prev.map(ev => ({ ...ev, isNew: false }))), 400);
    };
    const handleMatchFinished = ({ result }) => {
      playWhistle();
      stopCheerSound();
      setShowAmbulance(false);
      setAmbulanceData(null);
      setMatchFinished(true);
      setFinalResult(result);
      setCardChanges(result.cards || []);
    };
    const handleOpponentDisconnected = () => setOpponentLeft(true);
    const handleOpponentReconnected = () => setOpponentLeft(false);
    const handleMatchError = err => setToast({ message: err.error, type: 'error' });
    const handleMatchStrength = ({ home, away }) => {
      setTeamStrengths({ home: isHomeTeam ? home : away, away: isHomeTeam ? away : home });
    };
    const handleOpponentSquadUpdated = ({ opponentSquad: newOppSquad, opponentBench: newOppBench }) => {
      setOpponentSquad(enrichSquadWithPower(newOppSquad));
      setOpponentBench(enrichSquadWithPower(newOppBench));
    };
    const handlePhaseTimerPaused = ({ seconds }) => { setTimerFrozen(true); setPhaseTimer(seconds); };
    const handlePhaseTimerResumed = ({ seconds }) => { setTimerFrozen(false); setPhaseTimer(seconds); };
    const handleForcePhase = (data) => {
      if (data.phase === 'substitutions') { setPhase('substitutions'); setShowSubs(true); }
    };

    socket.on('phase-change', handlePhaseChange);
    socket.on('phase-timer', handlePhaseTimer);
    socket.on('match-event', handleMatchEvent);
    socket.on('match-finished', handleMatchFinished);
    socket.on('opponent-disconnected', handleOpponentDisconnected);
    socket.on('opponent-reconnected', handleOpponentReconnected);
    socket.on('resume-match', handleResumeMatch);
    socket.on('match-error', handleMatchError);
    socket.on('match-strength', handleMatchStrength);
    socket.on('opponent-squad-updated', handleOpponentSquadUpdated);
    socket.on('phase-timer-paused', handlePhaseTimerPaused);
    socket.on('phase-timer-resumed', handlePhaseTimerResumed);
    socket.on('force-phase', handleForcePhase);
    socket.on('final-squad', handleFinalSquad);
    socket.on('server-shutdown', handleServerShutdown);
    socket.emit('get-match-state');
    return () => {
      socket.off('phase-change', handlePhaseChange);
      socket.off('phase-timer', handlePhaseTimer);
      socket.off('match-event', handleMatchEvent);
      socket.off('match-finished', handleMatchFinished);
      socket.off('opponent-disconnected', handleOpponentDisconnected);
      socket.off('opponent-reconnected', handleOpponentReconnected);
      socket.off('resume-match', handleResumeMatch);
      socket.off('match-error', handleMatchError);
      socket.off('match-strength', handleMatchStrength);
      socket.off('opponent-squad-updated', handleOpponentSquadUpdated);
      socket.off('phase-timer-paused', handlePhaseTimerPaused);
      socket.off('phase-timer-resumed', handlePhaseTimerResumed);
      socket.off('force-phase', handleForcePhase);
      socket.off('final-squad', handleFinalSquad);
      socket.off('server-shutdown', handleServerShutdown);
      if (halfTimeAnimationTimer.current) clearTimeout(halfTimeAnimationTimer.current);
    };
  }, [socket, userId, isHomeTeam, homeUserId, awayUserId, redCardedPlayerIds, injuredPlayerIds, yourSquadRaw, opponentSquad, yourBench, opponentBench, initialMatchData.opponentId, playGoal, playWhistle, playRed, playAmbulance, startCheer, stopCheerSound, enrichSquadWithPower, t, handleResumeMatch, timerFrozen, hasUnsavedSubs, handleFinalSquad, handleServerShutdown, goalScreen, offsidePopup]);

  const handleSwap = useCallback((activeId, benchId) => {
    const activeIdx = yourSquadRaw.findIndex(p => p.id === activeId);
    const benchIdx = yourBench.findIndex(p => p.id === benchId);
    if (activeIdx === -1 || benchIdx === -1) return;

    const newActive = [...yourSquadRaw];
    const newBench = [...yourBench];
    const temp = newActive[activeIdx];
    newActive[activeIdx] = newBench[benchIdx];
    newBench[benchIdx] = temp;
    setYourSquadRaw(newActive);
    setYourBench(newBench);
    const activeIds = newActive.map(p => p.id);
    const benchIds = newBench.map(p => p.id);
    sendSubsToServer(activeIds, benchIds, myFormation);
  }, [yourSquadRaw, yourBench, myFormation, sendSubsToServer]);

  const handleEventPlayerClick = (playerId, team) => {
    setHighlightedPlayer({ id: playerId, team });
    setTimeout(() => setHighlightedPlayer(null), 3000);
  };

  const handleFormationChange = (formationId) => { sendFormationToServer(formationId); };
  const handlePlayingStyleChange = (styleId) => {
    setSelectedPlayingStyle(styleId);
    sendPlayingStyleToServer(styleId);
  };

  // ========== RENDER: Match Finished ==========
  if (matchFinished) {
    const getCardImage = card => getPlayerAvatarSrc(card);
    return (
      <div 
        className="min-h-screen text-white p-6 flex flex-col items-center justify-center relative overflow-hidden" 
        dir={i18n.dir()}
        style={{ background: theme.gradient }}
      >
        <TournamentBackground theme={theme} />
        <div className="relative z-10 w-full max-w-4xl">
          <motion.h1 
            initial={{ scale: 0.8 }} 
            animate={{ scale: 1 }} 
            className="text-5xl font-bold mb-4 text-center"
            style={{ color: theme.textSecondary, textShadow: `0 0 30px ${theme.glow}` }}
          >
            {theme.titleIcon} {t('match.matchFinished')}
          </motion.h1>
          <p className="text-3xl font-bold mb-2 text-center text-white">{finalResult.yourScore} – {finalResult.opponentScore}</p>
          <p className="text-lg mt-2 text-center" style={{ color: theme.textSecondary }}>
            {finalResult.winner === 'you' ? t('match.youWon') : finalResult.winner === 'opponent' ? t('match.youLost') : t('match.draw')}
          </p>
          <h3 className="text-xl mt-6 mb-3 flex items-center justify-center gap-2 text-white">
            {t('match.cardEvolution')} <span className="text-sm animate-pulse" style={{ color: theme.textSecondary }}>{t('match.statsUpdated')}</span>
          </h3>
          <div className="max-h-96 overflow-y-auto w-full grid grid-cols-1 md:grid-cols-2 gap-4">
            {cardChanges.map((card, idx) => {
              const hasChanges = card.diff && (card.diff.attack !== 0 || card.diff.defense !== 0 || card.diff.speed !== 0 || card.diff.accuracy !== 0);
              return (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, y: 30, scale: 0.9 }} 
                  animate={{ opacity: 1, y: 0, scale: 1 }} 
                  transition={{ delay: idx * 0.1, type: 'spring', stiffness: 200 }} 
                  className="rounded-xl p-4 border transition-all shadow-xl"
                  style={{
                    background: theme.cardBg,
                    borderColor: theme.cardBorder,
                    boxShadow: `0 0 20px ${theme.glow}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden ring-2" style={{ borderColor: theme.accent }}>
                        <img src={getCardImage(card)} alt={card.name} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.src = '/img/player.jpg'; }} />
                      </div>
                      <div>
                        <div className="font-bold text-white text-lg">{card.name}</div>
                        <div style={{ color: theme.textMuted }} className="text-xs">{card.position || 'Player'}</div>
                      </div>
                    </div>
                    {hasChanges && (
                      <motion.div 
                        initial={{ scale: 0 }} 
                        animate={{ scale: 1 }} 
                        transition={{ delay: idx * 0.1 + 0.3, type: 'spring' }} 
                        className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                        style={{ background: `${theme.accent}30`, color: theme.textSecondary }}
                      >
                        <span>✨</span> {t('match.updated')}
                      </motion.div>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <StatWithArrow value={card.attack} diff={card.diff?.attack} label={t('statistics.attack')} theme={theme} />
                    <StatWithArrow value={card.defense} diff={card.diff?.defense} label={t('statistics.defense')} theme={theme} />
                    <StatWithArrow value={card.speed} diff={card.diff?.speed} label={t('statistics.speed')} theme={theme} />
                    <StatWithArrow value={card.accuracy} diff={card.diff?.accuracy} label={t('statistics.accuracy')} theme={theme} />
                  </div>
                  {!hasChanges && (
                    <div className="text-center text-xs mt-2" style={{ color: theme.textMuted }}>{t('match.noChanges')}</div>
                  )}
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <button 
              onClick={onFinish} 
              className="px-8 py-3 rounded-full font-bold transition transform hover:scale-105 shadow-lg"
              style={{ background: theme.buttonBg, color: theme.buttonText, boxShadow: `0 0 20px ${theme.glow}` }}
            >
              {t('match.backToGame')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- RENDER: Substitution Screen ----------
  if (showSubs) {
    const filteredActive = yourSquad.filter(p => !redCardedPlayerIds.includes(p.id) && !injuredPlayerIds.includes(p.id));
    const filteredBench = yourBench.filter(p => !redCardedPlayerIds.includes(p.id) && !injuredPlayerIds.includes(p.id));
    const myScore = score.home;
    const oppScoreVal = score.away;
    const timeLeft = phaseTimer;

    return (
      <div 
        className="min-h-screen text-white p-4 md:p-8 font-sans relative overflow-hidden" 
        dir={i18n.dir()}
        style={{ background: theme.gradient }}
      >
        <TournamentBackground theme={theme} />
        <div className="relative z-10 max-w-6xl mx-auto">
          <TournamentBanner theme={theme} tournamentName={tournamentName} />
          
          <div className="text-center mb-6">
            <motion.div 
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="inline-block px-6 py-2 rounded-full backdrop-blur-sm border"
              style={{ background: theme.cardBg, borderColor: theme.accent, color: theme.textSecondary }}
            >
              <span className="font-semibold tracking-wide">{t('match.halfTime')}</span>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="backdrop-blur-md rounded-2xl border shadow-xl mb-6 overflow-hidden"
            style={{ background: theme.cardBg, borderColor: theme.cardBorder, boxShadow: `0 0 30px ${theme.glow}` }}
          >
            <div className="p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 text-center">
                  <div className="text-sm uppercase tracking-wider mb-1" style={{ color: theme.textMuted }}>🏠 {t('match.you')}</div>
                  <div className="text-4xl md:text-5xl font-bold tracking-tight text-white">{myScore}</div>
                </div>
                <div className="text-2xl font-black px-4" style={{ color: theme.textSecondary }}>VS</div>
                <div className="flex-1 text-center">
                  <div className="text-sm uppercase tracking-wider mb-1" style={{ color: theme.textMuted }}>✈️ {t('match.opponent')}</div>
                  <div className="text-4xl md:text-5xl font-bold tracking-tight text-white">{oppScoreVal}</div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t" style={{ borderColor: theme.cardBorder }}>
                <div className="flex justify-between text-xs mb-1">
                  <span style={{ color: theme.textMuted }}>{t('match.timeRemaining')}</span>
                  <span className="font-mono font-semibold" style={{ color: theme.textSecondary }}>{timeLeft}s</span>
                </div>
                <div className="w-full rounded-full h-1.5 overflow-hidden" style={{ background: `${theme.primary}80` }}>
                  <motion.div 
                    className="h-full rounded-full"
                    style={{ background: theme.headerBar }}
                    initial={{ width: '100%' }} 
                    animate={{ width: `${(timeLeft / 50) * 100}%` }} 
                    transition={{ duration: 0.5, ease: 'easeOut' }} 
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tactics UI (Segmented Control / Pill Tabs) */}
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            <div className="flex items-center gap-2 p-1 rounded-full backdrop-blur-sm border" 
                 style={{ background: 'rgba(0,0,0,0.3)', borderColor: theme.cardBorder }}>
              <span className="text-xs font-bold px-3 text-gray-400 uppercase tracking-wider hidden sm:block">
                {t('formation.strategy')}:
              </span>
              {formations.map(f => (
                <button 
                  key={f.id} 
                  onClick={() => handleFormationChange(f.id)} 
                  className={`px-3 sm:px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 ${
                    selectedFormation === f.id ? 'shadow-lg scale-105' : 'text-gray-400 hover:text-white'
                  }`}
                  style={{
                    background: selectedFormation === f.id ? theme.buttonBg : 'transparent',
                    color: selectedFormation === f.id ? theme.buttonText : 'inherit',
                    boxShadow: selectedFormation === f.id ? `0 0 15px ${theme.glow}` : 'none',
                  }}
                >
                  {t(f.nameKey)} <span className="opacity-60 ml-1 hidden sm:inline">({f.short})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Playing Styles (Segmented Control) */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 p-1 rounded-full backdrop-blur-sm border" 
                 style={{ background: 'rgba(0,0,0,0.3)', borderColor: theme.cardBorder }}>
              <span className="text-xs font-bold px-3 text-gray-400 uppercase tracking-wider hidden sm:block">
                {t('match.playingStyle')}:
              </span>
              {playingStyles.map(style => (
                <button 
                  key={style.id} 
                  onClick={() => handlePlayingStyleChange(style.id)} 
                  className={`px-3 sm:px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 ${
                    selectedPlayingStyle === style.id ? 'shadow-lg scale-105' : 'text-gray-400 hover:text-white'
                  }`}
                  style={{
                    background: selectedPlayingStyle === style.id ? theme.buttonBg : 'transparent',
                    color: selectedPlayingStyle === style.id ? theme.buttonText : 'inherit',
                    boxShadow: selectedPlayingStyle === style.id ? `0 0 15px ${theme.glow}` : 'none',
                  }}
                >
                  {style.icon} {t(style.nameKey)}
                </button>
              ))}
            </div>
          </div>

          <SubstitutionPitch
            activeSquad={filteredActive}
            benchSquad={filteredBench}
            onSwap={handleSwap}
            selectedOut={selectedOut}
            setSelectedOut={setSelectedOut}
            t={t}
            theme={theme}
          />

          <div className="text-center text-sm mt-4" style={{ color: theme.textMuted }}>
            {t('match.dragToSwap') || 'اسحب لاعب من الملعب إلى البدلاء أو العكس'}
          </div>
        </div>
      </div>
    );
  }

  // ---------- RENDER: Main Live Match ----------
  const isMobile = window.innerWidth < 768;
  const myStrength = isHomeTeam ? teamStrengths.home : teamStrengths.away;
  const oppStrength = isHomeTeam ? teamStrengths.away : teamStrengths.home;
  const displayTimer = phaseTimer;

  // ✅ حساب ارتفاع أعمدة اللاعبين بناءً على عدد اللاعبين
  const PLAYER_CARD_HEIGHT = 80;
  const HEADER_HEIGHT = 48;
  const PADDING = 24;
  
  const squadHeight = yourSquad.length * PLAYER_CARD_HEIGHT + HEADER_HEIGHT + PADDING;
  const opponentSquadHeight = opponentSquad.length * PLAYER_CARD_HEIGHT + HEADER_HEIGHT + PADDING;
  const columnsHeight = Math.max(squadHeight, opponentSquadHeight, 500);

  return (
    <div 
      className="min-h-screen text-white p-4 relative overflow-hidden" 
      dir={i18n.dir()}
      style={{ background: theme.gradient }}
    >
      <TournamentBackground theme={theme} />
      
      <AnimatePresence>
        {showHalfTimeAnimation && <HalfTimeAnimation onComplete={() => {}} theme={theme} />}
      </AnimatePresence>
      <AnimatePresence>
        {toast && <Toast key={toast.message + toast.type} message={toast.message} type={toast.type} onClose={() => setToast(null)} theme={theme} />}
      </AnimatePresence>
      <AnimatePresence>
        {goalScreen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md">
            <motion.div 
              initial={{ scale: 0.85, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.85, opacity: 0, y: 20 }} 
              transition={{ type: 'spring', stiffness: 260, damping: 22 }} 
              className="relative w-80 md:w-96"
            >
              <div className="absolute inset-0 -z-10 rounded-2xl blur-xl" style={{ background: theme.goalGradient }} />
              <div 
                className="relative overflow-hidden rounded-2xl border bg-white/5 backdrop-blur-xl shadow-2xl"
                style={{ borderColor: theme.accent, boxShadow: `0 0 40px ${theme.glow}` }}
              >
                <div className="h-1 w-full" style={{ background: theme.headerBar }} />
                <div className="p-6 text-center">
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }} 
                    animate={{ scale: 1, rotate: 0 }} 
                    transition={{ type: 'spring', stiffness: 140, damping: 10, delay: 0.1 }} 
                    className="mx-auto mb-4 text-6xl md:text-7xl drop-shadow-lg"
                  >
                    ⚽
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ delay: 0.2 }} 
                    className="mb-4 flex items-center justify-center gap-3"
                  >
                    <div 
                      className="flex h-14 w-14 items-center justify-center rounded-full text-2xl shadow-lg ring-2 ring-white/20 ring-offset-2 ring-offset-black/50 md:h-16 md:w-16 md:text-3xl"
                      style={{ background: theme.goalGradient }}
                    >
                      {goalScreen.position === 'Striker' && '⚡'}
                      {goalScreen.position === 'Midfielder' && '⚙️'}
                      {goalScreen.position === 'Defender' && '🛡️'}
                      {goalScreen.position === 'Goalkeeper' && '🧤'}
                      {!goalScreen.position && '⚽'}
                    </div>
                    <div className="text-left">
                      <div className="text-xl font-bold tracking-wide text-white md:text-2xl">{goalScreen.playerName}</div>
                      <div className="text-xs font-semibold uppercase tracking-widest md:text-sm" style={{ color: theme.textSecondary }}>
                        {goalScreen.position}
                      </div>
                    </div>
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }} 
                    animate={{ opacity: 1, scale: 1 }} 
                    transition={{ delay: 0.3 }} 
                    className="mx-auto mb-3 inline-flex items-center justify-center gap-2 rounded-full bg-black/40 px-4 py-2 text-lg font-bold text-white backdrop-blur-sm md:text-xl"
                  >
                    <span>{goalScreen.team === 'home' ? '🏠' : '✈️'}</span>
                    <span className="tracking-tight">{goalScreen.score.home} – {goalScreen.score.away}</span>
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    transition={{ delay: 0.4 }} 
                    className="text-sm font-medium uppercase tracking-wider"
                    style={{ color: '#ffffff' }}
                  >
                    🔥 {t('match.whatAStrike')} 🔥
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {offsidePopup && (
          <EventPopup
            key="offside-popup"
            type={offsidePopup.type}
            team={offsidePopup.team}
            player={offsidePopup.player}
            theme={theme}
            onComplete={() => {
              if (offsideTimerRef.current) {
                clearTimeout(offsideTimerRef.current);
                offsideTimerRef.current = null;
              }
              setOffsidePopup(null);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAmbulance && ambulanceData && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 30 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative w-80 md:w-96"
            >
              <div 
                className="absolute inset-0 -z-10 rounded-2xl blur-xl"
                style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.5) 0%, rgba(249,115,22,0.5) 100%)' }}
              />
              <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl shadow-2xl">
                <div className="h-1 w-full bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500" />
                <div className="p-6 text-center">
                  <button
                    onClick={closeAmbulance}
                    className="absolute top-2 right-2 text-white bg-black/50 rounded-full p-1 hover:bg-black/70 transition z-10"
                  >
                    ✕
                  </button>
                  <motion.div
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                    className="text-6xl md:text-7xl mb-4"
                  >
                    🚑
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-xl font-bold text-white"
                  >
                    {t('match.injuryAmbulance')}
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-sm mt-2"
                    style={{ color: theme.textSecondary }}
                  >
                    {ambulanceData.player && (
                      <span>{t('match.injuryPlayer', { player: ambulanceData.player })}</span>
                    )}
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto relative z-10">
        <TournamentBanner theme={theme} tournamentName={tournamentName} />

        {/* ===== شريط الاستحواذ ===== */}
        <div className="w-full mb-3">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span className="font-medium">{yourTeamName}</span>
            <span className="font-mono text-yellow-400">{Math.round(threatData.userThreat)}%</span>
            <span className="font-medium">{opponentTeamName}</span>
          </div>
          <div className="w-full h-3 bg-gray-700/50 rounded-full overflow-hidden shadow-inner">
            <motion.div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{ 
                width: `${threatData.userThreat}%`,
                background: `linear-gradient(to right, ${theme.homeTeamColor}, ${theme.awayTeamColor})`,
                boxShadow: `0 0 15px ${threatData.userThreat > 55 ? theme.homeTeamColor : theme.awayTeamColor}40`
              }}
              animate={{ width: `${threatData.userThreat}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
            <span>{t('match.defending')}</span>
            <span>
              {threatData.userThreat > 60 ? t('match.attacking') :
               threatData.userThreat < 40 ? t('match.defending') :
               t('match.balanced')}
            </span>
            <span>{t('match.attacking')}</span>
          </div>
        </div>
        
        <div className="w-full mb-6">
          <MiniPitch 
            homeSquad={yourSquad} 
            awaySquad={opponentSquad} 
            homeScore={score.home} 
            awayScore={score.away} 
            isLive={!showHalfTimeAnimation && !showSubs && !matchFinished}
            theme={theme}
            threatData={threatData}
          />
        </div>

        {/* ========== الشاشات الكبيرة والمتوسطة ========== */}
        {!isMobile ? (
          /* ✅ جميع الأعمدة لها نفس الارتفاع بالضبط */
          <div className="flex gap-4 items-stretch">
            
            {/* عمود فريقك - ارتفاع ثابت مساوي للأعمدة الأخرى */}
            <div 
              className="w-1/4 backdrop-blur-sm rounded-xl p-3 border flex flex-col"
              style={{
                background: theme.cardBg,
                borderColor: theme.cardBorder,
                boxShadow: `0 0 20px ${theme.glow}`,
                height: `${columnsHeight}px`,
              }}
            >
              <h3 className="text-lg font-bold mb-3 text-center flex-shrink-0" style={{ color: theme.homeTeamColor }}>
                🏠 {yourTeamName}
              </h3>
              {/* ✅ جميع اللاعبين يظهرون بدون تمرير */}
              <div className="space-y-2 flex-1 overflow-hidden">
                {yourSquad.map(player => (
                  <SquadPlayer 
                    key={player.id} 
                    player={player} 
                    stats={playerStats[player.id]} 
                    isHighlighted={highlightedPlayer?.id === player.id && highlightedPlayer?.team === 'home'} 
                    onClick={() => setHighlightedPlayer({ id: player.id, team: 'home' })} 
                    redCarded={redCardedPlayerIds.includes(player.id)}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
            
            {/* العمود الأوسط - Match Events - نفس الارتفاع مع تمرير داخلي */}
            <div 
              className="flex-1 flex flex-col rounded-xl border p-3"
              style={{
                height: `${columnsHeight}px`,
                background: theme.cardBg,
                borderColor: theme.cardBorder,
                boxShadow: `0 0 20px ${theme.glow}`,
              }}
            >
              {/* شريط النتيجة - ثابت لا يتمرر */}
              <div 
                className="rounded-lg p-4 mb-3 shadow-xl border flex-shrink-0"
                style={{
                  background: theme.cardBg,
                  borderColor: theme.cardBorder,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs" style={{ color: theme.textMuted }}>{t('match.yourFormation')}</p>
                    <p className="text-lg font-bold" style={{ color: theme.textSecondary }}>
                      {formations.find(f => f.id === myFormation) ? t(formations.find(f => f.id === myFormation).nameKey) : myFormation}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: theme.textMuted }}>{t('match.half')}</p>
                    <p className="text-2xl font-bold text-white">{half} / 2</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: theme.textMuted }}>{t('match.timeRemaining')}</p>
                    <p className="text-3xl font-mono" style={{ color: theme.textSecondary }}>{displayTimer}s</p>
                  </div>
                </div>
                <div className="flex justify-center gap-8 mt-3 pt-3 border-t" style={{ borderColor: theme.cardBorder }}>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{score.home}</p>
                    <p className="text-xs" style={{ color: theme.textMuted }}>{yourTeamName}</p>
                    <p className="text-xs" style={{ color: theme.homeTeamColor }}>⚡ {t('squadManager.strength')}: {myStrength}</p>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: theme.textSecondary }}>VS</div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{score.away}</p>
                    <p className="text-xs" style={{ color: theme.textMuted }}>{opponentTeamName}</p>
                    <p className="text-xs" style={{ color: theme.awayTeamColor }}>⚡ {t('squadManager.strength')}: {oppStrength}</p>
                  </div>
                </div>
              </div>
              
              {/* ✅ Match Events - بدون sticky title (يختفي عند التمرير) */}
              <div 
                className="flex-1 overflow-y-auto rounded-lg p-2"
                style={{
                  background: theme.cardBg,
                }}
              >
                <h3 className="font-bold mb-2 text-lg text-white">
                  📋 {t('match.events')}
                </h3>
                <div className="space-y-1">
                  {events.map((ev, idx) => (
                    <EventItem
                      key={`${ev.type}-${ev.second}-${idx}`}
                      event={ev}
                      userId={userId}
                      homeUserId={homeUserId}
                      awayUserId={awayUserId}
                      yourSquad={yourSquadRaw}
                      opponentSquad={opponentSquad}
                      yourBench={yourBench}
                      opponentBench={opponentBench}
                      onPlayerClick={handleEventPlayerClick}
                      isNew={ev.isNew}
                      t={t}
                      isHomeTeam={isHomeTeam}
                      opponentId={opponentId}
                      theme={theme}
                    />
                  ))}
                  <div ref={eventsEndRef} />
                </div>
              </div>
              
              {opponentLeft && (
                <div 
                  className="mt-3 rounded-lg p-3 text-center flex-shrink-0"
                  style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    color: '#fca5a5',
                  }}
                >
                  {t('match.opponentDisconnected')}
                </div>
              )}
            </div>
            
            {/* عمود الخصم - ارتفاع ثابت مساوي للأعمدة الأخرى */}
            <div 
              className="w-1/4 backdrop-blur-sm rounded-xl p-3 border flex flex-col"
              style={{
                background: theme.cardBg,
                borderColor: theme.cardBorder,
                boxShadow: `0 0 20px ${theme.glow}`,
                height: `${columnsHeight}px`,
              }}
            >
              <h3 className="text-lg font-bold mb-3 text-center flex-shrink-0" style={{ color: theme.awayTeamColor }}>
                ✈️ {opponentTeamName}
              </h3>
              {/* ✅ جميع اللاعبين يظهرون بدون تمرير */}
              <div className="space-y-2 flex-1 overflow-hidden">
                {opponentSquad.map(player => (
                  <SquadPlayer 
                    key={player.id} 
                    player={player} 
                    stats={playerStats[player.id]} 
                    isHighlighted={highlightedPlayer?.id === player.id && highlightedPlayer?.team === 'away'} 
                    onClick={() => setHighlightedPlayer({ id: player.id, team: 'away' })} 
                    redCarded={false}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ========== نسخة الموبايل ========== */
          <div className="flex flex-col gap-4">
            <div 
              className="rounded-xl p-3 border"
              style={{ background: theme.cardBg, borderColor: theme.cardBorder }}
            >
              <h3 className="text-md font-bold mb-2 text-center" style={{ color: theme.homeTeamColor }}>🏠 {yourTeamName}</h3>
              <div className="grid grid-cols-2 gap-2">
                {yourSquad.map(player => (
                  <SquadPlayer
                    key={player.id}
                    player={player}
                    stats={playerStats[player.id]}
                    isHighlighted={highlightedPlayer?.id === player.id && highlightedPlayer?.team === 'home'}
                    onClick={() => setHighlightedPlayer({ id: player.id, team: 'home' })}
                    redCarded={redCardedPlayerIds.includes(player.id)}
                    theme={theme}
                  />
                ))}
              </div>
            </div>

            <div 
              className="rounded-lg p-3 shadow-xl"
              style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs" style={{ color: theme.textMuted }}>{t('match.yourFormation')}</p>
                  <p className="text-sm font-bold" style={{ color: theme.textSecondary }}>
                    {formations.find(f => f.id === myFormation) ? t(formations.find(f => f.id === myFormation).nameKey) : myFormation}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: theme.textMuted }}>{t('match.half')}</p>
                  <p className="text-xl font-bold text-white">{half}/2</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: theme.textMuted }}>{t('match.timeRemaining')}</p>
                  <p className="text-xl font-mono" style={{ color: theme.textSecondary }}>{displayTimer}s</p>
                </div>
              </div>
              <div className="flex justify-center gap-6 mt-2">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{score.home}</p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>{yourTeamName}</p>
                  <p className="text-[10px]" style={{ color: theme.homeTeamColor }}>⚡{myStrength}</p>
                </div>
                <div className="text-2xl font-bold" style={{ color: theme.textSecondary }}>VS</div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{score.away}</p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>{opponentTeamName}</p>
                  <p className="text-[10px]" style={{ color: theme.awayTeamColor }}>⚡{oppStrength}</p>
                </div>
              </div>
            </div>

            {/* Match Events للموبايل - بدون sticky title */}
            <div 
              className="rounded-xl p-3" 
              style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                background: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`,
              }}
            >
              <h3 className="font-bold mb-2 text-sm text-white">📋 {t('match.events')}</h3>
              <div className="space-y-1">
                {events.map((ev, idx) => (
                  <EventItem
                    key={`${ev.type}-${ev.second}-${idx}`}
                    event={ev}
                    userId={userId}
                    homeUserId={homeUserId}
                    awayUserId={awayUserId}
                    yourSquad={yourSquadRaw}
                    opponentSquad={opponentSquad}
                    yourBench={yourBench}
                    opponentBench={opponentBench}
                    onPlayerClick={handleEventPlayerClick}
                    isNew={ev.isNew}
                    t={t}
                    isHomeTeam={isHomeTeam}
                    opponentId={opponentId}
                    theme={theme}
                  />
                ))}
                <div ref={eventsEndRef} />
              </div>
            </div>

            <div 
              className="rounded-xl p-3 border"
              style={{ background: theme.cardBg, borderColor: theme.cardBorder }}
            >
              <h3 className="text-md font-bold mb-2 text-center" style={{ color: theme.awayTeamColor }}>✈️ {opponentTeamName}</h3>
              <div className="grid grid-cols-2 gap-2">
                {opponentSquad.map(player => (
                  <SquadPlayer
                    key={player.id}
                    player={player}
                    stats={playerStats[player.id]}
                    isHighlighted={highlightedPlayer?.id === player.id && highlightedPlayer?.team === 'away'}
                    onClick={() => setHighlightedPlayer({ id: player.id, team: 'away' })}
                    redCarded={false}
                    theme={theme}
                  />
                ))}
              </div>
            </div>

            {opponentLeft && (
              <div 
                className="p-2 text-center text-sm rounded"
                style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5' }}
              >
                {t('match.opponentDisconnected')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveMatch;